/**
 * Data-access layer over Supabase.
 *
 * Each function returns a typed result with { data, error }. The store
 * calls these and converts errors to user-visible toasts.
 *
 * IMPORTANT: every mutation function expects auth to be valid. RLS on the
 * server enforces that mutations only succeed for rows in orgs the user
 * belongs to. If RLS denies, error is returned.
 */

import { supabase } from '@/lib/supabase';
import {
  DEFAULT_HEALTH_THRESHOLDS,
  normalizeHealthThresholds,
  type HealthThresholds,
} from '@/lib/health/thresholds';
import {
  DEFAULT_RESOURCE_MODE,
  normalizeResourceMode,
  type ResourceMode,
} from '@/lib/resourceMode';
import type {
  Project,
  Task,
  Risk,
  Issue,
  ChangeRequest,
  Baseline,
  Portfolio,
  PortfolioStatus,
  Program,
  ProgramStatus,
  BudgetLine,
  BudgetVersion,
  BudgetLinePeriod,
  WorkspacePhase,
  ProjectSizeTier,
  Decision,
  ActionItem,
  TimeEntry,
  Board,
  BoardColumn,
  WorkItemType,
  WorkItem,
  Sprint,
  SprintBurndownSnapshot,
  WorkingCalendar,
  WorkingCalendarException,
  InsightSnooze,
  InsightTargetKind,
  Benefit,
  BenefitCategory,
  BenefitStatus,
  Demand,
  DemandStage,
  DemandSettings,
  Lesson,
  LessonCategory,
  ResourceTimeOff,
  ResourceScenario,
  ScenarioOverride,
  UtilizationSnapshot,
  TimeOffKind,
  Resource,
  Requirement,
  RequirementCategory,
  RequirementPriority,
  RequirementStatus,
  RequirementTestCase,
  TestCaseStatus,
  ChangeRequestHistoryEntry,
  ChangeRequestStatus,
  GateTemplate,
  GateTemplateItem,
  ProjectGate,
  ProjectGateItem,
  ProjectGateHistoryEntry,
  ProjectGateStatus,
  AppNotification,
  NotificationKind,
  NotificationEntityType,
  ResourceRole,
  ResourceRoleAssignment,
  RateCard,
  ResourceSkillLevel,
  SkillLevel,
  Department,
  PipelineProject,
  PipelineProjectStatus,
  PipelineProjectPriority,
  PipelineResourceNeed,
} from '@/types';
import {
  fromProjectRow,
  toProjectRow,
  fromTaskRow,
  toTaskRow,
  fromRiskRow,
  toRiskRow,
  fromIssueRow,
  toIssueRow,
  fromChangeRequestRow,
  toChangeRequestRow,
  fromDecisionRow,
  toDecisionRow,
  fromActionItemRow,
  toActionItemRow,
  fromBaselineRow,
  toBaselineRow,
  fromBudgetLineRow,
  toBudgetLineRow,
  fromBudgetVersionRow,
  toBudgetVersionRow,
  fromBudgetLinePeriodRow,
  toBudgetLinePeriodRow,
  type ProjectRow,
  type TaskRow,
  type RiskRow,
  type IssueRow,
  type ChangeRequestRow,
  type DecisionRow,
  type ActionItemRow,
  type BaselineRow,
  type BudgetLineRow,
  type BudgetVersionRow,
  type BudgetLinePeriodRow,
} from './mappers';

export interface ApiResult<T> {
  data: T | null;
  error: string | null;
}

function err<T>(msg: string): ApiResult<T> {
  return { data: null, error: msg };
}

function ok<T>(data: T): ApiResult<T> {
  return { data, error: null };
}

const NOT_AUTH = 'Not authenticated. Please log in.';

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------

/**
 * Returns the user's first organization ID, or null if none.
 * (Multi-org switcher is Phase 14 — for now, users have one org each.)
 */
export async function getCurrentOrgId(): Promise<string | null> {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from('organization_members')
    .select('organization_id')
    .limit(1);
  if (error || !data || data.length === 0) return null;
  return data[0].organization_id as string;
}

/**
 * Returns the user's first organization (id + name + plan), or null.
 * Uses an inner join so the response includes the org row in one query.
 */
export interface CurrentOrg {
  id: string;
  name: string;
  plan: string;
  /** Phase 20.3: ISO 4217 currency code for monetary display */
  currency: string;
  /** The current user's role in this org: 'owner' | 'admin' | 'member' | 'viewer'.
   *  Undefined when unknown; treat undefined as an editor (non-viewer). */
  role?: string;
}

export async function getCurrentOrg(): Promise<ApiResult<CurrentOrg>> {
  if (!supabase) return err(NOT_AUTH);
  const { data: userData } = await supabase.auth.getUser();
  const uid = userData?.user?.id;
  if (!uid) return err(NOT_AUTH);
  // Scope to the CALLER's own membership row — RLS lets a user see every member
  // in their orgs, so without this filter a multi-member workspace returns many
  // rows (maybeSingle then throws "multiple rows") and the wrong role.
  const { data, error } = await supabase
    .from('organization_members')
    .select('organization_id, role, organizations!inner(id, name, plan, currency)')
    .eq('user_id', uid)
    .limit(1)
    .maybeSingle();
  if (error) return err(error.message);
  if (!data) return err('No organization found');
  // Supabase returns the joined row as an object (not array) when using
  // !inner with .single() / .maybeSingle()
  const row = data as unknown as { role: string; organizations: CurrentOrg };
  const org = row.organizations;
  if (!org) return err('No organization found');
  return ok({
    id: org.id,
    name: org.name,
    plan: org.plan,
    currency: org.currency || 'USD',
    role: row.role,
  });
}

/**
 * List ALL organizations the current user belongs to. Used by the org
 * switcher dropdown in the header.
 */
export async function listMyOrgs(): Promise<ApiResult<CurrentOrg[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data: userData } = await supabase.auth.getUser();
  const uid = userData?.user?.id;
  if (!uid) return err(NOT_AUTH);
  // Only the caller's own membership rows (one per org) — see getCurrentOrg note.
  const { data, error } = await supabase
    .from('organization_members')
    .select('organization_id, role, organizations!inner(id, name, plan, currency)')
    .eq('user_id', uid)
    .order('joined_at', { ascending: true });
  if (error) return err(error.message);
  if (!data) return ok([]);
  // Each row has shape { organization_id, role, organizations: {id, name, plan, currency} }
  const orgs = (data as unknown as Array<{ role: string; organizations: CurrentOrg }>)
    .filter((row) => Boolean(row.organizations))
    .map((row) => ({
      id: row.organizations.id,
      name: row.organizations.name,
      plan: row.organizations.plan,
      currency: row.organizations.currency || 'USD',
      role: row.role,
    }));
  return ok(orgs);
}

/**
 * Look up a specific org by id (only succeeds if user is a member).
 */
export async function getOrgById(orgId: string): Promise<ApiResult<CurrentOrg>> {
  if (!supabase) return err(NOT_AUTH);
  // Query via organization_members so we can also read the caller's role in the
  // org (needed for viewer read-only gating). Must scope to the caller's own
  // row — RLS exposes all members, so filtering only by org returns many rows
  // in a multi-member workspace and maybeSingle() throws.
  const { data: userData } = await supabase.auth.getUser();
  const uid = userData?.user?.id;
  if (!uid) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('organization_members')
    .select('role, organizations!inner(id, name, plan, currency)')
    .eq('organization_id', orgId)
    .eq('user_id', uid)
    .maybeSingle();
  if (error) return err(error.message);
  if (!data) return err('Organization not found');
  const row = data as unknown as { role: string; organizations: CurrentOrg };
  const org = row.organizations;
  if (!org) return err('Organization not found');
  return ok({
    id: org.id,
    name: org.name,
    plan: org.plan,
    currency: org.currency || 'USD',
    role: row.role,
  });
}

/**
 * Phase 20.3: Update an organization's currency.
 * RLS allows any member to update; future enhancement may scope to admin/owner.
 */
export async function updateOrgCurrency(
  orgId: string,
  currency: string,
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase
    .from('organizations')
    .update({ currency })
    .eq('id', orgId);
  if (error) return err(error.message);
  return ok(null);
}

export async function listProjects(orgId?: string): Promise<ApiResult<Project[]>> {
  if (!supabase) return err(NOT_AUTH);
  let query = supabase
    .from('projects')
    .select('*')
    .order('created_at', { ascending: false });
  if (orgId) {
    query = query.eq('organization_id', orgId);
  }
  const { data, error } = await query;
  if (error) return err(error.message);
  return ok((data as ProjectRow[]).map(fromProjectRow));
}

export async function createProject(
  orgId: string,
  project: Project,
): Promise<ApiResult<Project>> {
  if (!supabase) return err(NOT_AUTH);
  const row = { ...toProjectRow(project), organization_id: orgId };
  const { data, error } = await supabase
    .from('projects')
    .insert(row)
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Insert failed');
  return ok(fromProjectRow(data as ProjectRow));
}

export async function updateProject(
  id: string,
  updates: Partial<Project>,
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const row = { ...toProjectRow(updates), updated_at: new Date().toISOString() };
  const { error } = await supabase.from('projects').update(row).eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

export async function deleteProject(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('projects').delete().eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------

export async function listTasksForProject(
  projectId: string,
): Promise<ApiResult<Task[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('tasks')
    .select('*')
    .eq('project_id', projectId)
    .order('task_id', { ascending: true });
  if (error) return err(error.message);
  return ok((data as TaskRow[]).map(fromTaskRow));
}

export async function upsertTasks(
  projectId: string,
  tasks: Task[],
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  if (tasks.length === 0) return ok(null);
  const rows = tasks.map((t) => toTaskRow(projectId, t));
  const { error } = await supabase.from('tasks').upsert(rows, {
    onConflict: 'project_id,task_id',
  });
  if (error) return err(error.message);
  return ok(null);
}

/**
 * Replace ALL tasks for a project with the given set (delete-then-upsert).
 * Used by schedule import, which loads a fresh schedule over the current one
 * (a plain upsert would leave orphaned rows for tasks no longer present).
 */
export async function replaceTasks(
  projectId: string,
  tasks: Task[],
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error: delErr } = await supabase.from('tasks').delete().eq('project_id', projectId);
  if (delErr) return err(delErr.message);
  if (tasks.length === 0) return ok(null);
  const rows = tasks.map((t) => toTaskRow(projectId, t));
  const { error } = await supabase.from('tasks').upsert(rows, {
    onConflict: 'project_id,task_id',
  });
  if (error) return err(error.message);
  return ok(null);
}

export async function deleteTask(
  projectId: string,
  taskId: number,
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase
    .from('tasks')
    .delete()
    .eq('project_id', projectId)
    .eq('task_id', taskId);
  if (error) return err(error.message);
  return ok(null);
}

// ---------------------------------------------------------------------------
// Risks
// ---------------------------------------------------------------------------

export async function listRisksForProject(
  projectId: string,
): Promise<ApiResult<Risk[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('risks')
    .select('*')
    .eq('project_id', projectId);
  if (error) return err(error.message);
  return ok((data as RiskRow[]).map(fromRiskRow));
}

export async function upsertRisk(
  projectId: string,
  risk: Risk,
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const row = toRiskRow(projectId, risk);
  const { error } = await supabase.from('risks').upsert(row, { onConflict: 'id' });
  if (error) return err(error.message);
  return ok(null);
}

export async function deleteRisk(
  riskId: string,
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('risks').delete().eq('id', riskId);
  if (error) return err(error.message);
  return ok(null);
}

// ---------------------------------------------------------------------------
// Phase 30.9 (Budget B2) — Budget lines
// ---------------------------------------------------------------------------
//
// The budget_lines table uses a composite primary key (project_id, id) — see
// migration 0019. This means upserts MUST use `onConflict: 'project_id,id'`
// rather than `onConflict: 'id'` alone. This is the Phase 30.5 lesson baked
// in at the schema level from the start — cross-project ID collisions are
// impossible because the conflict key includes project_id.
//
// Phase 30.12: budget_lines PK changed to (project_id, budget_version_id, id).
// Upserts use the new composite onConflict; deletes filter on all three.

export async function listBudgetLinesForProject(
  projectId: string,
): Promise<ApiResult<BudgetLine[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('budget_lines')
    .select('*')
    .eq('project_id', projectId);
  if (error) return err(error.message);
  return ok((data as BudgetLineRow[]).map(fromBudgetLineRow));
}

export async function upsertBudgetLine(
  projectId: string,
  line: BudgetLine,
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const row = toBudgetLineRow(projectId, line);
  // Phase 30.12: composite PK now includes budget_version_id; onConflict must
  // mention all three columns. Without this, an upsert keyed on (project_id,id)
  // would try to use the OLD PK (which no longer exists post-migration).
  const { error } = await supabase
    .from('budget_lines')
    .upsert(row, { onConflict: 'project_id,budget_version_id,id' });
  if (error) return err(error.message);
  return ok(null);
}

export async function deleteBudgetLine(
  projectId: string,
  versionId: string,
  lineId: string,
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  // Phase 30.12: also filter on budget_version_id so we delete the exact
  // line, not all versions' copies of a line with that id.
  const { error } = await supabase
    .from('budget_lines')
    .delete()
    .eq('project_id', projectId)
    .eq('budget_version_id', versionId)
    .eq('id', lineId);
  if (error) return err(error.message);
  return ok(null);
}

/** Bulk upsert budget lines in one call (used by the budget/cost importer). */
export async function upsertBudgetLines(
  projectId: string,
  lines: BudgetLine[],
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  if (lines.length === 0) return ok(null);
  const rows = lines.map((l) => toBudgetLineRow(projectId, l));
  const { error } = await supabase
    .from('budget_lines')
    .upsert(rows, { onConflict: 'project_id,budget_version_id,id' });
  if (error) return err(error.message);
  return ok(null);
}

/** Delete every budget line in a version (used by the importer's Replace mode). */
export async function deleteBudgetLinesForVersion(
  projectId: string,
  versionId: string,
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase
    .from('budget_lines')
    .delete()
    .eq('project_id', projectId)
    .eq('budget_version_id', versionId);
  if (error) return err(error.message);
  return ok(null);
}

// ---------------------------------------------------------------------------
// Phase 30.12 (Budget B3) — Budget versions
// ---------------------------------------------------------------------------

export async function listBudgetVersionsForProject(
  projectId: string,
): Promise<ApiResult<BudgetVersion[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('budget_versions')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: true });
  if (error) return err(error.message);
  return ok((data as BudgetVersionRow[]).map(fromBudgetVersionRow));
}

export async function upsertBudgetVersion(
  projectId: string,
  version: BudgetVersion,
  createdBy?: string | null,
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const row = toBudgetVersionRow(projectId, version, createdBy);
  const { error } = await supabase
    .from('budget_versions')
    .upsert(row, { onConflict: 'project_id,id' });
  if (error) return err(error.message);
  return ok(null);
}

export async function deleteBudgetVersion(
  projectId: string,
  versionId: string,
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase
    .from('budget_versions')
    .delete()
    .eq('project_id', projectId)
    .eq('id', versionId);
  if (error) return err(error.message);
  return ok(null);
}

// ---------------------------------------------------------------------------
// Phase 30.15 (Budget B4) — Budget line periods (time-phased)
// ---------------------------------------------------------------------------

export async function listBudgetLinePeriodsForProject(
  projectId: string,
): Promise<ApiResult<BudgetLinePeriod[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('budget_line_periods')
    .select('*')
    .eq('project_id', projectId)
    .order('period_start', { ascending: true });
  if (error) return err(error.message);
  return ok((data as BudgetLinePeriodRow[]).map(fromBudgetLinePeriodRow));
}

export async function upsertBudgetLinePeriod(
  projectId: string,
  period: BudgetLinePeriod,
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const row = toBudgetLinePeriodRow(projectId, period);
  // Composite PK includes all four columns; onConflict must match.
  const { error } = await supabase
    .from('budget_line_periods')
    .upsert(row, {
      onConflict: 'project_id,budget_version_id,budget_line_id,period_start',
    });
  if (error) return err(error.message);
  return ok(null);
}

export async function deleteBudgetLinePeriod(
  projectId: string,
  versionId: string,
  lineId: string,
  periodStart: string,
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase
    .from('budget_line_periods')
    .delete()
    .eq('project_id', projectId)
    .eq('budget_version_id', versionId)
    .eq('budget_line_id', lineId)
    .eq('period_start', periodStart);
  if (error) return err(error.message);
  return ok(null);
}

// ---------------------------------------------------------------------------
// Issues
// ---------------------------------------------------------------------------

export async function listIssuesForProject(
  projectId: string,
): Promise<ApiResult<Issue[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('issues')
    .select('*')
    .eq('project_id', projectId);
  if (error) return err(error.message);
  return ok((data as IssueRow[]).map(fromIssueRow));
}

export async function upsertIssue(
  projectId: string,
  issue: Issue,
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const row = toIssueRow(projectId, issue);
  const { error } = await supabase.from('issues').upsert(row, { onConflict: 'id' });
  if (error) return err(error.message);
  return ok(null);
}

export async function deleteIssue(issueId: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('issues').delete().eq('id', issueId);
  if (error) return err(error.message);
  return ok(null);
}

// ---------------------------------------------------------------------------
// Change Requests
// ---------------------------------------------------------------------------

export async function listChangeRequestsForProject(
  projectId: string,
): Promise<ApiResult<ChangeRequest[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('change_requests')
    .select('*')
    .eq('project_id', projectId);
  if (error) return err(error.message);
  return ok((data as ChangeRequestRow[]).map(fromChangeRequestRow));
}

export async function upsertChangeRequest(
  projectId: string,
  cr: ChangeRequest,
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const row = toChangeRequestRow(projectId, cr);
  const { error } = await supabase
    .from('change_requests')
    .upsert(row, { onConflict: 'id' });
  if (error) return err(error.message);
  return ok(null);
}

export async function deleteChangeRequest(
  crId: string,
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('change_requests').delete().eq('id', crId);
  if (error) return err(error.message);
  return ok(null);
}

// ---------------------------------------------------------------------------
// Decisions (Migration 0055) — mirror change_requests, no approval workflow
// ---------------------------------------------------------------------------

export async function listDecisionsForProject(
  projectId: string,
): Promise<ApiResult<Decision[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('decisions')
    .select('*')
    .eq('project_id', projectId);
  if (error) return err(error.message);
  return ok((data as DecisionRow[]).map(fromDecisionRow));
}

export async function upsertDecision(
  projectId: string,
  d: Decision,
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const row = toDecisionRow(projectId, d);
  const { error } = await supabase.from('decisions').upsert(row, { onConflict: 'id' });
  if (error) return err(error.message);
  return ok(null);
}

export async function deleteDecision(decisionId: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('decisions').delete().eq('id', decisionId);
  if (error) return err(error.message);
  return ok(null);
}

// ---------------------------------------------------------------------------
// Action items (Migration 0056) — mirror decisions
// ---------------------------------------------------------------------------

export async function listActionItemsForProject(
  projectId: string,
): Promise<ApiResult<ActionItem[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('action_items')
    .select('*')
    .eq('project_id', projectId);
  if (error) return err(error.message);
  return ok((data as ActionItemRow[]).map(fromActionItemRow));
}

export async function upsertActionItem(
  projectId: string,
  a: ActionItem,
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const row = toActionItemRow(projectId, a);
  const { error } = await supabase.from('action_items').upsert(row, { onConflict: 'id' });
  if (error) return err(error.message);
  return ok(null);
}

export async function deleteActionItem(actionItemId: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('action_items').delete().eq('id', actionItemId);
  if (error) return err(error.message);
  return ok(null);
}

// ---------------------------------------------------------------------------
// Baselines
// ---------------------------------------------------------------------------

export async function listBaselinesForProject(
  projectId: string,
): Promise<ApiResult<Baseline[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('baselines')
    .select('*')
    .eq('project_id', projectId);
  if (error) return err(error.message);
  return ok((data as BaselineRow[]).map(fromBaselineRow));
}

export async function upsertBaseline(
  projectId: string,
  baseline: Baseline,
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const row = toBaselineRow(projectId, baseline);
  const { error } = await supabase.from('baselines').upsert(row, { onConflict: 'id' });
  if (error) return err(error.message);
  return ok(null);
}

export async function deleteBaseline(
  baselineId: string,
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('baselines').delete().eq('id', baselineId);
  if (error) return err(error.message);
  return ok(null);
}

// ---------------------------------------------------------------------------
// Aggregator: load a full project (project + tasks + risks + issues + CRs + baselines)
// ---------------------------------------------------------------------------

/**
 * Load all of a project's data in parallel and merge into a complete
 * domain Project object.
 */
export async function loadProjectFull(
  projectId: string,
): Promise<ApiResult<Project>> {
  if (!supabase) return err(NOT_AUTH);

  const { data: projectRow, error: pErr } = await supabase
    .from('projects')
    .select('*')
    .eq('id', projectId)
    .maybeSingle();
  if (pErr) return err(pErr.message);
  if (!projectRow) return err('Project not found');

  const project = fromProjectRow(projectRow as ProjectRow);

  // Fetch children in parallel
  const [
    tasksRes,
    risksRes,
    issuesRes,
    crsRes,
    decisionsRes,
    actionItemsRes,
    baselinesRes,
    budgetLinesRes,
    budgetVersionsRes,
    budgetLinePeriodsRes,
  ] = await Promise.all([
    listTasksForProject(projectId),
    listRisksForProject(projectId),
    listIssuesForProject(projectId),
    listChangeRequestsForProject(projectId),
    listDecisionsForProject(projectId),
    listActionItemsForProject(projectId),
    listBaselinesForProject(projectId),
    listBudgetLinesForProject(projectId),
    listBudgetVersionsForProject(projectId),
    listBudgetLinePeriodsForProject(projectId),
  ]);

  // Surface the first error encountered (if any) but still return what we got
  const firstError =
    tasksRes.error ??
    risksRes.error ??
    issuesRes.error ??
    crsRes.error ??
    decisionsRes.error ??
    actionItemsRes.error ??
    baselinesRes.error ??
    budgetLinesRes.error ??
    budgetVersionsRes.error ??
    budgetLinePeriodsRes.error;

  project.wbsTasks = tasksRes.data ?? [];
  project.risks = risksRes.data ?? [];
  project.issues = issuesRes.data ?? [];
  project.changes = crsRes.data ?? [];
  project.decisions = decisionsRes.data ?? [];
  project.actionItems = actionItemsRes.data ?? [];
  project.baselines = baselinesRes.data ?? [];
  project.budgetLines = budgetLinesRes.data ?? [];
  project.budgetVersions = budgetVersionsRes.data ?? [];
  project.budgetLinePeriods = budgetLinePeriodsRes.data ?? [];

  if (firstError) {
    return { data: project, error: firstError };
  }
  return ok(project);
}

/**
 * Load all projects with their full child data (tasks, risks, etc).
 * Used on app bootstrap when a logged-in user opens the app.
 *
 * Optimized: fetches children in parallel rather than serially.
 */
export async function loadAllProjectsFull(orgId?: string): Promise<ApiResult<Project[]>> {
  if (!supabase) return err(NOT_AUTH);

  // First get the list of project IDs (filtered to a specific org if provided)
  const projectsRes = await listProjects(orgId);
  if (projectsRes.error || !projectsRes.data) return projectsRes as ApiResult<Project[]>;
  const projects = projectsRes.data;

  if (projects.length === 0) return ok([]);

  // Fetch children for each project in parallel.
  // For larger portfolios we could batch by org rather than per-project,
  // but this is fine up to a few dozen projects.
  const fullProjects = await Promise.all(
    projects.map(async (p) => {
      const [
        tasksRes,
        risksRes,
        issuesRes,
        crsRes,
        decisionsRes,
        actionItemsRes,
        baselinesRes,
        budgetLinesRes,
        budgetVersionsRes,
        budgetLinePeriodsRes,
      ] = await Promise.all([
        listTasksForProject(p.id),
        listRisksForProject(p.id),
        listIssuesForProject(p.id),
        listChangeRequestsForProject(p.id),
        listDecisionsForProject(p.id),
        listActionItemsForProject(p.id),
        listBaselinesForProject(p.id),
        listBudgetLinesForProject(p.id),
        listBudgetVersionsForProject(p.id),
        listBudgetLinePeriodsForProject(p.id),
      ]);
      return {
        ...p,
        wbsTasks: tasksRes.data ?? [],
        risks: risksRes.data ?? [],
        issues: issuesRes.data ?? [],
        changes: crsRes.data ?? [],
        decisions: decisionsRes.data ?? [],
        actionItems: actionItemsRes.data ?? [],
        baselines: baselinesRes.data ?? [],
        budgetLines: budgetLinesRes.data ?? [],
        budgetVersions: budgetVersionsRes.data ?? [],
        budgetLinePeriods: budgetLinePeriodsRes.data ?? [],
      };
    }),
  );

  return ok(fullProjects);
}

/**
 * Invoke the seed_sample_project Postgres RPC.
 * Returns the newly created project_id on success.
 */
export async function seedSampleProject(
  orgId: string,
): Promise<ApiResult<string>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase.rpc('seed_sample_project', {
    p_org_id: orgId,
  });
  if (error) return err(error.message);
  return ok(data as string);
}

// ===========================================================================
// Team / Workspace (Phase 14)
// ===========================================================================

export interface OrgMember {
  user_id: string;
  email: string;
  role: string;
  joined_at: string;
}

export interface OrgInvite {
  id: string;
  email: string;
  role: string;
  invited_by: string | null;
  created_at: string;
  accepted_at: string | null;
  token: string;
}

export interface InviteMetadata {
  organization_id: string;
  organization_name: string;
  email: string;
  role: string;
  inviter_email: string;
  created_at: string;
  already_accepted: boolean;
}

/** List all members of the given organization (with their emails). */
export async function listMembers(orgId: string): Promise<ApiResult<OrgMember[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('members_with_email')
    .select('user_id, email, role, joined_at')
    .eq('organization_id', orgId)
    .order('joined_at', { ascending: true });
  if (error) return err(error.message);
  return ok((data ?? []) as OrgMember[]);
}

/** List pending (unaccepted) invites for the given organization. */
export async function listPendingInvites(
  orgId: string,
): Promise<ApiResult<OrgInvite[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('organization_invites')
    .select('id, email, role, invited_by, created_at, accepted_at, token')
    .eq('organization_id', orgId)
    .is('accepted_at', null)
    .order('created_at', { ascending: false });
  if (error) return err(error.message);
  return ok((data ?? []) as OrgInvite[]);
}

/**
 * Result of inviteMember — includes both the created invite and the email
 * delivery status. If emailSent is false, the invite still exists in the
 * database; the user can fall back to copying the link manually.
 */
export interface InviteResult {
  invite: OrgInvite;
  emailSent: boolean;
  emailError: string | null;
}

/**
 * Create a new pending invite, then trigger an invite email via the
 * `send-invite-email` Edge Function.
 *
 * The function call is best-effort: if it fails (Edge Function not deployed,
 * Resend down, etc.) the invite is still created and the link still works.
 * The caller can show a fallback "Copy link" UI.
 */
export async function inviteMember(
  orgId: string,
  email: string,
  role: 'admin' | 'member' = 'member',
): Promise<ApiResult<InviteResult>> {
  if (!supabase) return err(NOT_AUTH);

  // Get caller's user id for invited_by
  const { data: userData } = await supabase.auth.getUser();
  const inviterId = userData?.user?.id ?? null;

  // Step 1: create the invite row
  const { data: inviteRow, error: insertErr } = await supabase
    .from('organization_invites')
    .insert({
      organization_id: orgId,
      email: email.trim().toLowerCase(),
      role,
      invited_by: inviterId,
    })
    .select()
    .single();

  if (insertErr || !inviteRow) {
    return err(insertErr?.message ?? 'Could not create invite');
  }

  const invite = inviteRow as OrgInvite;

  // Step 2: invoke the Edge Function to send the email
  let emailSent = false;
  let emailError: string | null = null;

  try {
    const { error: fnErr } = await supabase.functions.invoke('send-invite-email', {
      body: { invite_id: invite.id },
    });
    if (fnErr) {
      emailError = fnErr.message;
    } else {
      emailSent = true;
    }
  } catch (e) {
    emailError = e instanceof Error ? e.message : String(e);
  }

  return ok({ invite, emailSent, emailError });
}

/**
 * Re-trigger sending the invite email for an already-created invite.
 * Useful for "Resend email" actions in the pending invites list.
 */
export async function resendInviteEmail(inviteId: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  try {
    const { error: fnErr } = await supabase.functions.invoke('send-invite-email', {
      body: { invite_id: inviteId },
    });
    if (fnErr) return err(fnErr.message);
    return ok(null);
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return err(msg);
  }
}

/** Cancel (delete) a pending invite. */
export async function cancelInvite(inviteId: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase
    .from('organization_invites')
    .delete()
    .eq('id', inviteId);
  if (error) return err(error.message);
  return ok(null);
}

/** Remove a member from the organization. */
export async function removeMember(
  orgId: string,
  userId: string,
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.rpc('remove_member', {
    p_org_id: orgId,
    p_user_id: userId,
  });
  if (error) return err(error.message);
  return ok(null);
}

/**
 * Phase 26: change a member's role (owner-only, enforced server-side by the
 * set_member_role RPC). Toggles a member between 'admin' and 'member'.
 * Does not handle ownership transfer — newRole may only be 'admin' or 'member'.
 */
export async function setMemberRole(
  orgId: string,
  userId: string,
  newRole: 'admin' | 'member' | 'viewer',
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.rpc('set_member_role', {
    p_org_id: orgId,
    p_user_id: userId,
    p_new_role: newRole,
  });
  if (error) return err(error.message);
  return ok(null);
}

/**
 * Phase 28: transfer workspace ownership to another member.
 *
 * Atomic handoff (enforced server-side by the transfer_ownership RPC): the
 * target member becomes 'owner' and the calling owner becomes 'admin'. The
 * caller must currently be an owner. This is a handoff, not a co-ownership
 * grant. The caller (Settings) gates this behind a confirmation modal.
 */
export async function transferOwnership(
  orgId: string,
  newOwnerUserId: string,
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.rpc('transfer_ownership', {
    p_org_id: orgId,
    p_new_owner_user_id: newOwnerUserId,
  });
  if (error) return err(error.message);
  return ok(null);
}

/**
 * Phase 27: permanently delete a workspace and all its data.
 *
 * Owner-only and refuses if an active paid subscription exists — both are
 * enforced server-side by the delete_workspace RPC; this is just the client
 * wrapper. DESTRUCTIVE AND IRREVERSIBLE. The caller (Settings) gates this
 * behind a type-to-confirm modal.
 */
export async function deleteWorkspace(orgId: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.rpc('delete_workspace', {
    p_org_id: orgId,
  });
  if (error) return err(error.message);
  return ok(null);
}

/**
 * Look up an invite by its token, without requiring authentication.
 * Used by the accept-invite page to display "You've been invited to <Org>".
 */
export async function getInviteByToken(
  token: string,
): Promise<ApiResult<InviteMetadata>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .rpc('get_invite_by_token', { p_token: token })
    .single();
  if (error) return err(error.message);
  if (!data) return err('Invite not found');
  return ok(data as InviteMetadata);
}

/** Accept an invite by its token. Caller must be authenticated with a matching email. */
export async function acceptInvite(
  token: string,
): Promise<ApiResult<string>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase.rpc('accept_invite', {
    p_token: token,
  });
  if (error) return err(error.message);
  return ok(data as string);
}

/** Rename the workspace. */
export async function renameOrganization(
  orgId: string,
  newName: string,
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.rpc('rename_organization', {
    p_org_id: orgId,
    p_new_name: newName,
  });
  if (error) return err(error.message);
  return ok(null);
}

// ===========================================================================
// Project Documents (Phase 16)
// ===========================================================================

export interface ProjectDocument {
  id: string;
  project_id: string;
  uploaded_by: string | null;
  storage_path: string;
  filename: string;
  size_bytes: number;
  mime_type: string | null;
  uploaded_at: string;
}

const DOCS_BUCKET = 'project-documents';

/**
 * Maximum allowed file size in bytes (must match the bucket's file_size_limit
 * configured in migration 0005). Surfacing this constant lets the UI validate
 * before attempting upload.
 */
export const MAX_DOCUMENT_SIZE_BYTES = 25 * 1024 * 1024; // 25 MB

/**
 * Allowed MIME types for upload (must match the bucket's allowed_mime_types).
 * The frontend validates against this list to give immediate feedback before
 * attempting upload.
 */
export const ALLOWED_DOCUMENT_MIME_TYPES = new Set<string>([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv',
  'application/rtf',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'application/zip',
  'application/x-zip-compressed',
]);

/** List documents for a project. */
export async function listDocuments(
  projectId: string,
): Promise<ApiResult<ProjectDocument[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('project_documents')
    .select('*')
    .eq('project_id', projectId)
    .order('uploaded_at', { ascending: false });
  if (error) return err(error.message);
  return ok((data ?? []) as ProjectDocument[]);
}

/**
 * Phase 22: List all documents across all projects in a given organization.
 *
 * RLS handles row-level filtering: the query joins through projects to
 * verify the document's project belongs to an org the user is a member of.
 * We rely on the policy in 0005_project_documents.sql which scopes selects
 * to projects in the user's orgs.
 *
 * To filter by org without exposing a server-side join, we pre-fetch the
 * project IDs for the requested org and use a single `in()` filter. This is
 * one extra query but keeps the data path simple and avoids a Postgres view.
 *
 * For very large workspaces (1000+ projects), consider paginating or a
 * server-side function. The typical mid-market workspace (50-500 projects)
 * is fine with this approach.
 */
export async function listOrgDocuments(
  orgId: string,
): Promise<ApiResult<ProjectDocument[]>> {
  if (!supabase) return err(NOT_AUTH);

  // Resolve project IDs for this org first
  const { data: projData, error: projErr } = await supabase
    .from('projects')
    .select('id')
    .eq('organization_id', orgId);
  if (projErr) return err(projErr.message);
  const projectIds = (projData ?? []).map((r: { id: string }) => r.id);
  if (projectIds.length === 0) return ok([]);

  // Fetch all documents for those projects
  const { data, error } = await supabase
    .from('project_documents')
    .select('*')
    .in('project_id', projectIds)
    .order('uploaded_at', { ascending: false });
  if (error) return err(error.message);
  return ok((data ?? []) as ProjectDocument[]);
}

/**
 * Upload a file to storage AND create the metadata row.
 * If either step fails, this attempts to roll back the other to avoid
 * orphaned storage objects or orphaned rows.
 */
export async function uploadDocument(
  projectId: string,
  file: File,
): Promise<ApiResult<ProjectDocument>> {
  if (!supabase) return err(NOT_AUTH);

  // Frontend validation (server enforces too via bucket config)
  if (file.size > MAX_DOCUMENT_SIZE_BYTES) {
    return err(`File too large (${formatBytes(file.size)}). Max ${formatBytes(MAX_DOCUMENT_SIZE_BYTES)}.`);
  }
  if (file.type && !ALLOWED_DOCUMENT_MIME_TYPES.has(file.type)) {
    return err(`File type "${file.type}" is not allowed.`);
  }

  // Build a unique path: {project_id}/{file_uuid}-{safe_filename}
  // Sanitize the filename for storage path: keep alphanumerics, dot, dash, underscore
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 100);
  const fileUuid = crypto.randomUUID();
  const storagePath = `${projectId}/${fileUuid}-${safeName}`;

  // 1. Upload to storage
  const { error: uploadErr } = await supabase.storage
    .from(DOCS_BUCKET)
    .upload(storagePath, file, {
      contentType: file.type || 'application/octet-stream',
      upsert: false,
    });

  if (uploadErr) return err(uploadErr.message);

  // 2. Get caller's user id for uploaded_by
  const { data: userData } = await supabase.auth.getUser();
  const uploaderId = userData?.user?.id ?? null;

  // 3. Create the metadata row
  const { data, error: insertErr } = await supabase
    .from('project_documents')
    .insert({
      project_id: projectId,
      uploaded_by: uploaderId,
      storage_path: storagePath,
      filename: file.name,
      size_bytes: file.size,
      mime_type: file.type || null,
    })
    .select()
    .single();

  if (insertErr) {
    // Roll back the storage upload — best effort
    await supabase.storage.from(DOCS_BUCKET).remove([storagePath]);
    return err(insertErr.message);
  }

  return ok(data as ProjectDocument);
}

/**
 * Total document-storage bytes used by an org (summed across all its projects).
 * Backs the Settings usage meter and the pre-upload quota check. The
 * authoritative enforcement is the server-side trigger from migration 0057;
 * this is a best-effort UX read (RPC is guarded to the caller's own orgs).
 */
export async function getOrgStorageBytes(orgId: string): Promise<ApiResult<number>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase.rpc('org_document_storage_bytes', { p_org_id: orgId });
  if (error) return err(error.message);
  return ok(Number(data ?? 0));
}

/**
 * Generate a short-lived signed URL for downloading a file.
 * URLs expire after 60 seconds — long enough to start a download, short
 * enough that copy-paste sharing is unproductive.
 */
export async function getDocumentDownloadUrl(
  storagePath: string,
): Promise<ApiResult<string>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase.storage
    .from(DOCS_BUCKET)
    .createSignedUrl(storagePath, 60); // 60 seconds
  if (error || !data) return err(error?.message ?? 'Could not get download URL');
  return ok(data.signedUrl);
}

/**
 * Delete both the storage object AND the metadata row.
 * If the storage delete fails, we still attempt the row delete (cleaner than
 * leaving a row pointing to a missing file). User sees a warning in that case.
 */
export async function deleteDocument(
  doc: ProjectDocument,
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);

  // Delete storage object first (RLS-aware)
  const { error: storageErr } = await supabase.storage
    .from(DOCS_BUCKET)
    .remove([doc.storage_path]);

  // Even if storage delete fails (file already gone, etc), try the row delete
  const { error: rowErr } = await supabase
    .from('project_documents')
    .delete()
    .eq('id', doc.id);

  if (rowErr) return err(rowErr.message);
  if (storageErr) {
    // Row deleted, storage didn't — file is orphaned. Log but don't error.
    console.warn('[ProjexHQ] Storage delete failed (orphan possible):', storageErr.message);
  }
  return ok(null);
}

/** Helper: format byte count for display. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** Public formatter for the UI. */
export const formatFileSize = formatBytes;

// ===========================================================================
// Portfolios (Phase 18)
// ===========================================================================

export interface PortfolioRow {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  manager: string | null;
  status: string;
  start_date: string | null;
  end_date: string | null;
  target_budget: number;
  created_at: string;
  updated_at: string;
}

export function fromPortfolioRow(row: PortfolioRow): Portfolio {
  return {
    id: row.id,
    name: row.name,
    description: row.description ?? '',
    manager: row.manager ?? '',
    status: row.status as PortfolioStatus,
    startDate: row.start_date,
    endDate: row.end_date,
    targetBudget: Number(row.target_budget ?? 0),
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function toPortfolioRow(p: Partial<Portfolio>): Partial<PortfolioRow> {
  const r: Partial<PortfolioRow> = {};
  if (p.id !== undefined) r.id = p.id;
  if (p.name !== undefined) r.name = p.name;
  if (p.description !== undefined) r.description = p.description;
  if (p.manager !== undefined) r.manager = p.manager;
  if (p.status !== undefined) r.status = p.status;
  if (p.startDate !== undefined) r.start_date = p.startDate;
  if (p.endDate !== undefined) r.end_date = p.endDate;
  if (p.targetBudget !== undefined) r.target_budget = p.targetBudget;
  return r;
}

/** List all portfolios in the active org (RLS filters automatically). */
export async function listPortfolios(orgId?: string): Promise<ApiResult<Portfolio[]>> {
  if (!supabase) return err(NOT_AUTH);
  let q = supabase
    .from('portfolios')
    .select('*')
    .order('created_at', { ascending: false });
  if (orgId) q = q.eq('organization_id', orgId);
  const { data, error } = await q;
  if (error) return err(error.message);
  return ok((data as PortfolioRow[]).map(fromPortfolioRow));
}

/** Get a single portfolio by id. */
export async function getPortfolio(id: string): Promise<ApiResult<Portfolio>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('portfolios')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) return err(error.message);
  if (!data) return err('Portfolio not found');
  return ok(fromPortfolioRow(data as PortfolioRow));
}

/** Create a new portfolio in the given org. */
export async function createPortfolio(
  orgId: string,
  portfolio: Portfolio,
): Promise<ApiResult<Portfolio>> {
  if (!supabase) return err(NOT_AUTH);
  const row = { ...toPortfolioRow(portfolio), organization_id: orgId };
  const { data, error } = await supabase
    .from('portfolios')
    .insert(row)
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Insert failed');
  return ok(fromPortfolioRow(data as PortfolioRow));
}

/** Update a portfolio (partial). */
export async function updatePortfolio(
  id: string,
  updates: Partial<Portfolio>,
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase
    .from('portfolios')
    .update(toPortfolioRow(updates))
    .eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

/** Delete a portfolio. Member projects become unallocated (FK SET NULL). */
export async function deletePortfolio(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('portfolios').delete().eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

/**
 * Bulk-assign projects to a portfolio. Pass null to unassign.
 * Returns the count of rows updated.
 */
export async function assignProjectsToPortfolio(
  projectIds: string[],
  portfolioId: string | null,
): Promise<ApiResult<number>> {
  if (!supabase) return err(NOT_AUTH);
  if (projectIds.length === 0) return ok(0);
  const { error, count } = await supabase
    .from('projects')
    .update({ portfolio_id: portfolioId }, { count: 'exact' })
    .in('id', projectIds);
  if (error) return err(error.message);
  return ok(count ?? 0);
}

// ===========================================================================
// Programs (Projects → Programs → Portfolios) — mirrors Portfolios
// ===========================================================================

export interface ProgramRow {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  manager: string | null;
  status: string;
  start_date: string | null;
  end_date: string | null;
  target_budget: number;
  portfolio_id: string | null;
  created_at: string;
  updated_at: string;
}

export function fromProgramRow(row: ProgramRow): Program {
  return {
    id: row.id,
    name: row.name,
    description: row.description ?? '',
    manager: row.manager ?? '',
    status: row.status as ProgramStatus,
    startDate: row.start_date,
    endDate: row.end_date,
    targetBudget: Number(row.target_budget ?? 0),
    portfolioId: row.portfolio_id,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function toProgramRow(p: Partial<Program>): Partial<ProgramRow> {
  const r: Partial<ProgramRow> = {};
  if (p.id !== undefined) r.id = p.id;
  if (p.name !== undefined) r.name = p.name;
  if (p.description !== undefined) r.description = p.description;
  if (p.manager !== undefined) r.manager = p.manager;
  if (p.status !== undefined) r.status = p.status;
  if (p.startDate !== undefined) r.start_date = p.startDate;
  if (p.endDate !== undefined) r.end_date = p.endDate;
  if (p.targetBudget !== undefined) r.target_budget = p.targetBudget;
  if (p.portfolioId !== undefined) r.portfolio_id = p.portfolioId ?? null;
  return r;
}

/** List all programs in the active org (RLS filters automatically). */
export async function listPrograms(orgId?: string): Promise<ApiResult<Program[]>> {
  if (!supabase) return err(NOT_AUTH);
  let q = supabase.from('programs').select('*').order('created_at', { ascending: false });
  if (orgId) q = q.eq('organization_id', orgId);
  const { data, error } = await q;
  if (error) return err(error.message);
  return ok((data as ProgramRow[]).map(fromProgramRow));
}

export async function getProgram(id: string): Promise<ApiResult<Program>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase.from('programs').select('*').eq('id', id).maybeSingle();
  if (error) return err(error.message);
  if (!data) return err('Program not found');
  return ok(fromProgramRow(data as ProgramRow));
}

export async function createProgram(orgId: string, program: Program): Promise<ApiResult<Program>> {
  if (!supabase) return err(NOT_AUTH);
  const row = { ...toProgramRow(program), organization_id: orgId };
  const { data, error } = await supabase.from('programs').insert(row).select().single();
  if (error || !data) return err(error?.message ?? 'Insert failed');
  return ok(fromProgramRow(data as ProgramRow));
}

export async function updateProgram(id: string, updates: Partial<Program>): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('programs').update(toProgramRow(updates)).eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

/** Delete a program. Member projects become program-unallocated (FK SET NULL). */
export async function deleteProgram(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('programs').delete().eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

/** Bulk-assign projects to a program. Pass null to unassign. */
export async function assignProjectsToProgram(
  projectIds: string[],
  programId: string | null,
): Promise<ApiResult<number>> {
  if (!supabase) return err(NOT_AUTH);
  if (projectIds.length === 0) return ok(0);
  const { error, count } = await supabase
    .from('projects')
    .update({ program_id: programId }, { count: 'exact' })
    .in('id', projectIds);
  if (error) return err(error.message);
  return ok(count ?? 0);
}

// ---------------------------------------------------------------------------
// Phase 30.18 — AI ("ask-project-ai" Edge Function)
// ---------------------------------------------------------------------------

export interface AskProjectAIResponse {
  responseText: string;
  inputTokens: number;
  outputTokens: number;
  costCents: number;
  latencyMs: number;
}

/**
 * Ask the AI a question about a project.
 *
 * Calls the ask-project-ai Edge Function. The Edge Function:
 *   - Authenticates the user via JWT
 *   - Loads the project + portfolio context server-side
 *   - Calls Anthropic Claude Sonnet 4.6
 *   - Logs the call to ai_usage_logs (cost tracking)
 *   - Returns the response text + usage metrics
 *
 * Failures return a non-2xx response with an error message; we surface that
 * to the caller via the ApiResult error field.
 */
export async function askProjectAI(
  projectId: string,
  promptText: string,
): Promise<ApiResult<AskProjectAIResponse>> {
  if (!supabase) return err(NOT_AUTH);
  try {
    const { data, error } = await supabase.functions.invoke('ask-project-ai', {
      body: { promptText, projectId },
    });
    if (error) {
      // supabase-js wraps non-2xx Edge Function responses in this error.
      // The body (if JSON) is in error.context.
      // deno-lint-ignore no-explicit-any
      const ctx = (error as any).context;
      const msg =
        ctx?.error ?? error.message ?? 'AI request failed';
      return err(typeof msg === 'string' ? msg : 'AI request failed');
    }
    if (!data || typeof data.responseText !== 'string') {
      return err('AI returned an unexpected response shape');
    }
    return ok(data as AskProjectAIResponse);
  } catch (e) {
    return err(e instanceof Error ? e.message : String(e));
  }
}

// ===========================================================================
// Phase 30.20: Resource capacity registry (resource heat map)
// ===========================================================================

export interface ResourceCapacity {
  /** Lowercased / collapsed-whitespace key. Matches src/lib/resources.ts normalizeName(). */
  nameNormalized: string;
  /** Casing the user typed. */
  displayName: string;
  /** Effective full-week hours. Defaults to 40 server-side. */
  hoursPerWeek: number;
  /** Blended hourly cost rate in the workspace currency. Null = not set. */
  hourlyRate: number | null;
  /** True = deactivated (excluded from the active pool, kept for reporting). */
  isArchived: boolean;
  notes: string | null;
  updatedAt: string;
}

interface ResourceCapacityRow {
  organization_id: string;
  name_normalized: string;
  display_name: string;
  hours_per_week: number;
  hourly_rate: number | null;
  is_archived: boolean | null;
  notes: string | null;
  updated_at: string;
}

function fromResourceCapacityRow(r: ResourceCapacityRow): ResourceCapacity {
  return {
    nameNormalized: r.name_normalized,
    displayName: r.display_name,
    hoursPerWeek: r.hours_per_week,
    hourlyRate: r.hourly_rate ?? null,
    isArchived: r.is_archived ?? false,
    notes: r.notes,
    updatedAt: r.updated_at,
  };
}

/** Phase 30.20: list capacity rows for one org. Missing rows = default 40 hrs/wk. */
export async function listResourceCapacities(
  orgId: string,
): Promise<ApiResult<ResourceCapacity[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('resource_capacity')
    .select('*')
    .eq('organization_id', orgId);
  if (error) return err(error.message);
  return ok((data as ResourceCapacityRow[]).map(fromResourceCapacityRow));
}

/**
 * Phase 30.20: upsert capacity for one resource. Setting hoursPerWeek=40 with
 * no notes is the default and could omit the row entirely; we still write it
 * so the user has an explicit acknowledgement of what was set.
 */
export async function upsertResourceCapacity(
  orgId: string,
  c: { nameNormalized: string; resourceId?: string | null; displayName: string; hoursPerWeek: number; hourlyRate?: number | null; isArchived?: boolean; notes?: string | null },
): Promise<ApiResult<ResourceCapacity>> {
  if (!supabase) return err(NOT_AUTH);
  const row = {
    organization_id: orgId,
    name_normalized: c.nameNormalized,
    resource_id: c.resourceId ?? null,
    display_name: c.displayName,
    hours_per_week: c.hoursPerWeek,
    hourly_rate: c.hourlyRate ?? null,
    is_archived: c.isArchived ?? false,
    notes: c.notes ?? null,
  };
  const { data, error } = await supabase
    .from('resource_capacity')
    .upsert(row, { onConflict: 'organization_id,name_normalized' })
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Upsert failed');
  return ok(fromResourceCapacityRow(data as ResourceCapacityRow));
}

/** Phase 30.20: delete a capacity row (resets to default 40 hrs/wk). */
export async function deleteResourceCapacity(
  orgId: string,
  nameNormalized: string,
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase
    .from('resource_capacity')
    .delete()
    .eq('organization_id', orgId)
    .eq('name_normalized', nameNormalized);
  if (error) return err(error.message);
  return ok(null);
}

// ---------------------------------------------------------------------------
// Resource time off (Availability / PTO)
// ---------------------------------------------------------------------------

interface ResourceTimeOffRow {
  id: string;
  organization_id: string;
  name_normalized: string;
  display_name: string;
  start_date: string;
  end_date: string;
  kind: string;
  note: string | null;
  created_at: string;
  updated_at: string;
}

function fromResourceTimeOffRow(r: ResourceTimeOffRow): ResourceTimeOff {
  return {
    id: r.id,
    organizationId: r.organization_id,
    nameNormalized: r.name_normalized,
    displayName: r.display_name,
    startDate: r.start_date,
    endDate: r.end_date,
    kind: r.kind as TimeOffKind,
    note: r.note ?? undefined,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export async function listResourceTimeOff(orgId: string): Promise<ApiResult<ResourceTimeOff[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('resource_time_off')
    .select('*')
    .eq('organization_id', orgId)
    .order('start_date', { ascending: true });
  if (error) return err(error.message);
  return ok((data as ResourceTimeOffRow[]).map(fromResourceTimeOffRow));
}

export async function createResourceTimeOff(
  orgId: string,
  d: { nameNormalized: string; resourceId?: string | null; displayName: string; startDate: string; endDate: string; kind: TimeOffKind; note?: string | null },
): Promise<ApiResult<ResourceTimeOff>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('resource_time_off')
    .insert({
      organization_id: orgId,
      name_normalized: d.nameNormalized,
      resource_id: d.resourceId ?? null,
      display_name: d.displayName,
      start_date: d.startDate,
      end_date: d.endDate,
      kind: d.kind,
      note: d.note ?? null,
    })
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Create failed');
  return ok(fromResourceTimeOffRow(data as ResourceTimeOffRow));
}

export async function deleteResourceTimeOff(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('resource_time_off').delete().eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

// ---------------------------------------------------------------------------
// Global resource registry
// ---------------------------------------------------------------------------

interface ResourceRow {
  id: string;
  organization_id: string;
  name_normalized: string;
  display_name: string;
  user_id: string | null;
  email: string | null;
  role: string | null;
  department: string | null;
  skills: string[] | null;
  grade: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

function fromResourceRow(r: ResourceRow): Resource {
  return {
    id: r.id,
    organizationId: r.organization_id,
    nameNormalized: r.name_normalized,
    displayName: r.display_name,
    userId: r.user_id ?? undefined,
    email: r.email ?? undefined,
    role: r.role ?? undefined,
    department: r.department ?? undefined,
    skills: r.skills ?? [],
    grade: r.grade ?? undefined,
    isActive: r.is_active,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export async function listResources(orgId: string): Promise<ApiResult<Resource[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('resources')
    .select('*')
    .eq('organization_id', orgId)
    .order('display_name', { ascending: true });
  if (error) return err(error.message);
  return ok((data as ResourceRow[]).map(fromResourceRow));
}

/** Renames a person everywhere in one database operation. Refuses a name already in use. */
export async function renameResource(id: string, newName: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.rpc('rename_resource', { p_resource_id: id, p_new_name: newName });
  if (error) return err(error.message);
  return ok(null);
}

/** Moves everything from one person to another and removes the first, in one database operation. */
export async function mergeResource(fromId: string, intoId: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.rpc('merge_resource', { p_from_id: fromId, p_into_id: intoId });
  if (error) return err(error.message);
  return ok(null);
}

export async function createResource(
  orgId: string,
  d: { nameNormalized: string; displayName: string; userId?: string | null; email?: string | null; role?: string | null; department?: string | null; grade?: string | null; isActive?: boolean },
): Promise<ApiResult<Resource>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('resources')
    .insert({
      organization_id: orgId,
      name_normalized: d.nameNormalized,
      display_name: d.displayName,
      user_id: d.userId ?? null,
      email: d.email ?? null,
      role: d.role ?? null,
      department: d.department ?? null,
      grade: d.grade ?? null,
      is_active: d.isActive ?? true,
    })
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Create failed');
  return ok(fromResourceRow(data as ResourceRow));
}

export async function updateResource(
  id: string,
  patch: Partial<{ nameNormalized: string; displayName: string; userId: string | null; email: string | null; role: string | null; department: string | null; grade: string | null; skills: string[]; isActive: boolean }>,
): Promise<ApiResult<Resource>> {
  if (!supabase) return err(NOT_AUTH);
  const row: Record<string, unknown> = {};
  if (patch.nameNormalized !== undefined) row.name_normalized = patch.nameNormalized;
  if (patch.displayName !== undefined) row.display_name = patch.displayName;
  if (patch.userId !== undefined) row.user_id = patch.userId;
  if (patch.email !== undefined) row.email = patch.email;
  if (patch.role !== undefined) row.role = patch.role;
  if (patch.department !== undefined) row.department = patch.department;
  if (patch.grade !== undefined) row.grade = patch.grade;
  if (patch.skills !== undefined) row.skills = patch.skills;
  if (patch.isActive !== undefined) row.is_active = patch.isActive;
  const { data, error } = await supabase
    .from('resources')
    .update(row)
    .eq('id', id)
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Update failed');
  return ok(fromResourceRow(data as ResourceRow));
}

export async function deleteResource(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('resources').delete().eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

// --- What-if scenarios (Migration 0063) ---------------------------------------

interface ScenarioRow {
  id: string;
  organization_id: string;
  name: string;
  overrides: ScenarioOverride[] | null;
  created_at: string;
  updated_at: string;
}

function fromScenarioRow(r: ScenarioRow): ResourceScenario {
  return {
    id: r.id,
    organizationId: r.organization_id,
    name: r.name,
    overrides: r.overrides ?? [],
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export async function listScenarios(orgId: string): Promise<ApiResult<ResourceScenario[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('resource_scenarios')
    .select('*')
    .eq('organization_id', orgId)
    .order('updated_at', { ascending: false });
  if (error) return err(error.message);
  return ok((data as ScenarioRow[]).map(fromScenarioRow));
}

export async function createScenario(
  orgId: string,
  name: string,
  overrides: ScenarioOverride[],
): Promise<ApiResult<ResourceScenario>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('resource_scenarios')
    .insert({ organization_id: orgId, name, overrides })
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Create failed');
  return ok(fromScenarioRow(data as ScenarioRow));
}

export async function updateScenario(
  id: string,
  patch: Partial<{ name: string; overrides: ScenarioOverride[] }>,
): Promise<ApiResult<ResourceScenario>> {
  if (!supabase) return err(NOT_AUTH);
  const row: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.name !== undefined) row.name = patch.name;
  if (patch.overrides !== undefined) row.overrides = patch.overrides;
  const { data, error } = await supabase
    .from('resource_scenarios')
    .update(row)
    .eq('id', id)
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Update failed');
  return ok(fromScenarioRow(data as ScenarioRow));
}

export async function deleteScenario(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('resource_scenarios').delete().eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

// --- Utilisation snapshots (Migration 0064) -----------------------------------

interface UtilSnapshotRow {
  id: string;
  organization_id: string;
  captured_on: string;
  resource_key: string;
  display_name: string;
  utilization_pct: number;
}

function fromUtilSnapshotRow(r: UtilSnapshotRow): UtilizationSnapshot {
  return {
    id: r.id,
    organizationId: r.organization_id,
    capturedOn: r.captured_on,
    resourceKey: r.resource_key,
    displayName: r.display_name,
    utilizationPct: Number(r.utilization_pct),
  };
}

export async function listUtilizationSnapshots(orgId: string): Promise<ApiResult<UtilizationSnapshot[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('resource_utilization_snapshots')
    .select('*')
    .eq('organization_id', orgId)
    .order('captured_on', { ascending: true });
  if (error) return err(error.message);
  return ok((data as UtilSnapshotRow[]).map(fromUtilSnapshotRow));
}

export async function insertUtilizationSnapshots(
  orgId: string,
  capturedOn: string,
  rows: { resourceKey: string; resourceId?: string | null; displayName: string; utilizationPct: number }[],
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  if (rows.length === 0) return ok(null);
  const { error } = await supabase.from('resource_utilization_snapshots').upsert(
    rows.map((r) => ({
      organization_id: orgId,
      captured_on: capturedOn,
      resource_key: r.resourceKey,
      resource_id: r.resourceId ?? null,
      display_name: r.displayName,
      utilization_pct: r.utilizationPct,
    })),
    { onConflict: 'organization_id,captured_on,resource_key' },
  );
  if (error) return err(error.message);
  return ok(null);
}

// ===========================================================================
// Phase 30.23: Workspace lifecycle phases (Owner/Admin-managed)
// ===========================================================================

interface WorkspacePhaseRow {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  sort_order: number;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
}

function fromWorkspacePhaseRow(r: WorkspacePhaseRow): WorkspacePhase {
  return {
    id: r.id,
    organizationId: r.organization_id,
    name: r.name,
    description: r.description ?? undefined,
    sortOrder: r.sort_order,
    isArchived: r.is_archived,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export async function listWorkspacePhases(
  orgId: string,
): Promise<ApiResult<WorkspacePhase[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('workspace_phases')
    .select('*')
    .eq('organization_id', orgId)
    .order('sort_order', { ascending: true });
  if (error) return err(error.message);
  return ok((data as WorkspacePhaseRow[]).map(fromWorkspacePhaseRow));
}

export async function createWorkspacePhase(
  orgId: string,
  p: { name: string; description?: string; sortOrder?: number },
): Promise<ApiResult<WorkspacePhase>> {
  if (!supabase) return err(NOT_AUTH);
  const row = {
    organization_id: orgId,
    name: p.name,
    description: p.description ?? null,
    sort_order: p.sortOrder ?? 0,
  };
  const { data, error } = await supabase
    .from('workspace_phases')
    .insert(row)
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Create failed');
  return ok(fromWorkspacePhaseRow(data as WorkspacePhaseRow));
}

export async function updateWorkspacePhase(
  id: string,
  patch: { name?: string; description?: string | null; sortOrder?: number; isArchived?: boolean },
): Promise<ApiResult<WorkspacePhase>> {
  if (!supabase) return err(NOT_AUTH);
  const row: Record<string, unknown> = {};
  if (patch.name !== undefined) row.name = patch.name;
  if (patch.description !== undefined) row.description = patch.description;
  if (patch.sortOrder !== undefined) row.sort_order = patch.sortOrder;
  if (patch.isArchived !== undefined) row.is_archived = patch.isArchived;
  const { data, error } = await supabase
    .from('workspace_phases')
    .update(row)
    .eq('id', id)
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Update failed');
  return ok(fromWorkspacePhaseRow(data as WorkspacePhaseRow));
}

export async function deleteWorkspacePhase(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('workspace_phases').delete().eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

// ===========================================================================
// Migration 0054: Project size tiers (PMO-configurable) — mirrors phases above
// ===========================================================================

interface ProjectSizeTierRow {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  sort_order: number;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
}

function fromProjectSizeTierRow(r: ProjectSizeTierRow): ProjectSizeTier {
  return {
    id: r.id,
    organizationId: r.organization_id,
    name: r.name,
    description: r.description ?? undefined,
    sortOrder: r.sort_order,
    isArchived: r.is_archived,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export async function listProjectSizeTiers(
  orgId: string,
): Promise<ApiResult<ProjectSizeTier[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('project_size_tiers')
    .select('*')
    .eq('organization_id', orgId)
    .order('sort_order', { ascending: true });
  if (error) return err(error.message);
  return ok((data as ProjectSizeTierRow[]).map(fromProjectSizeTierRow));
}

export async function createProjectSizeTier(
  orgId: string,
  p: { name: string; description?: string; sortOrder?: number },
): Promise<ApiResult<ProjectSizeTier>> {
  if (!supabase) return err(NOT_AUTH);
  const row = {
    organization_id: orgId,
    name: p.name,
    description: p.description ?? null,
    sort_order: p.sortOrder ?? 0,
  };
  const { data, error } = await supabase
    .from('project_size_tiers')
    .insert(row)
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Create failed');
  return ok(fromProjectSizeTierRow(data as ProjectSizeTierRow));
}

export async function updateProjectSizeTier(
  id: string,
  patch: { name?: string; description?: string | null; sortOrder?: number; isArchived?: boolean },
): Promise<ApiResult<ProjectSizeTier>> {
  if (!supabase) return err(NOT_AUTH);
  const row: Record<string, unknown> = {};
  if (patch.name !== undefined) row.name = patch.name;
  if (patch.description !== undefined) row.description = patch.description;
  if (patch.sortOrder !== undefined) row.sort_order = patch.sortOrder;
  if (patch.isArchived !== undefined) row.is_archived = patch.isArchived;
  const { data, error } = await supabase
    .from('project_size_tiers')
    .update(row)
    .eq('id', id)
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Update failed');
  return ok(fromProjectSizeTierRow(data as ProjectSizeTierRow));
}

export async function deleteProjectSizeTier(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('project_size_tiers').delete().eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

// ===========================================================================
// Phase 30.24: Time entries (planned vs actual)
// ===========================================================================

interface TimeEntryRow {
  id: string;
  organization_id: string;
  user_id: string;
  project_id: string;
  task_id: string | null;
  task_name_snapshot: string;
  entry_date: string;
  hours: number | string;
  description: string | null;
  created_at: string;
  updated_at: string;
}

function fromTimeEntryRow(r: TimeEntryRow): TimeEntry {
  return {
    id: r.id,
    organizationId: r.organization_id,
    userId: r.user_id,
    projectId: r.project_id,
    taskId: r.task_id,
    taskNameSnapshot: r.task_name_snapshot,
    entryDate: r.entry_date,
    hours: Number(r.hours),
    description: r.description ?? undefined,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export interface ListTimeEntriesFilters {
  /** If set, only return entries for these projects */
  projectIds?: string[];
  /** ISO yyyy-mm-dd (inclusive lower bound) */
  startDate?: string;
  /** ISO yyyy-mm-dd (inclusive upper bound) */
  endDate?: string;
  /** If set, only return entries belonging to this user */
  userId?: string;
  /** Hard cap; default 500 */
  limit?: number;
}

export async function listTimeEntries(
  orgId: string,
  filters: ListTimeEntriesFilters = {},
): Promise<ApiResult<TimeEntry[]>> {
  if (!supabase) return err(NOT_AUTH);
  let q = supabase
    .from('time_entries')
    .select('*')
    .eq('organization_id', orgId)
    .order('entry_date', { ascending: false })
    .order('created_at', { ascending: false });
  if (filters.projectIds && filters.projectIds.length > 0) {
    q = q.in('project_id', filters.projectIds);
  }
  if (filters.startDate) q = q.gte('entry_date', filters.startDate);
  if (filters.endDate) q = q.lte('entry_date', filters.endDate);
  if (filters.userId) q = q.eq('user_id', filters.userId);
  q = q.limit(filters.limit ?? 500);
  const { data, error } = await q;
  if (error) return err(error.message);
  return ok((data as TimeEntryRow[]).map(fromTimeEntryRow));
}

export async function createTimeEntry(
  orgId: string,
  e: {
    projectId: string;
    taskId: string | null;
    taskNameSnapshot: string;
    entryDate: string;
    hours: number;
    description?: string;
  },
): Promise<ApiResult<TimeEntry>> {
  if (!supabase) return err(NOT_AUTH);
  const { data: userData, error: userErr } = await supabase.auth.getUser();
  if (userErr || !userData?.user) return err(NOT_AUTH);

  const row = {
    organization_id: orgId,
    user_id: userData.user.id,
    project_id: e.projectId,
    task_id: e.taskId,
    task_name_snapshot: e.taskNameSnapshot,
    entry_date: e.entryDate,
    hours: e.hours,
    description: e.description ?? null,
  };
  const { data, error } = await supabase
    .from('time_entries')
    .insert(row)
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Create failed');
  return ok(fromTimeEntryRow(data as TimeEntryRow));
}

export async function updateTimeEntry(
  id: string,
  patch: { hours?: number; description?: string | null; entryDate?: string; taskId?: string | null; taskNameSnapshot?: string },
): Promise<ApiResult<TimeEntry>> {
  if (!supabase) return err(NOT_AUTH);
  const row: Record<string, unknown> = {};
  if (patch.hours !== undefined) row.hours = patch.hours;
  if (patch.description !== undefined) row.description = patch.description;
  if (patch.entryDate !== undefined) row.entry_date = patch.entryDate;
  if (patch.taskId !== undefined) row.task_id = patch.taskId;
  if (patch.taskNameSnapshot !== undefined) row.task_name_snapshot = patch.taskNameSnapshot;
  const { data, error } = await supabase
    .from('time_entries')
    .update(row)
    .eq('id', id)
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Update failed');
  return ok(fromTimeEntryRow(data as TimeEntryRow));
}

export async function deleteTimeEntry(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('time_entries').delete().eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

/**
 * Phase 30.24: fetch the total actual hours per task for a project. Used by
 * Schedule tab and Overview KPI to show planned-vs-actual variance.
 *
 * Returns a map of task_id -> total hours. Tasks with no logged time are
 * omitted; consumers should treat missing keys as 0.
 */
export async function getProjectActualHoursByTask(
  projectId: string,
): Promise<ApiResult<Record<string, number>>> {
  if (!supabase) return err(NOT_AUTH);
  // Supabase doesn't expose SUM via PostgREST without a SQL view, so we
  // fetch all entries for the project and aggregate client-side. For
  // workspaces with thousands of entries this becomes a perf consideration;
  // address with a materialized view or RPC if it becomes an issue.
  const { data, error } = await supabase
    .from('time_entries')
    .select('task_id, hours')
    .eq('project_id', projectId)
    .not('task_id', 'is', null);
  if (error) return err(error.message);
  const out: Record<string, number> = {};
  for (const r of (data ?? []) as Array<{ task_id: string; hours: number | string }>) {
    if (!r.task_id) continue;
    out[r.task_id] = (out[r.task_id] ?? 0) + Number(r.hours);
  }
  return ok(out);
}

/**
 * Phase 3 EVM: total logged hours per user (auth user id) for a project, so
 * EVM can cost labor as Σ hours × that person's resolved rate. Like
 * getProjectActualHoursByTask, aggregates client-side (PostgREST has no SUM).
 * Entries with no task are still counted — any logged hour is labor cost.
 */
export async function getProjectActualHoursByUser(
  projectId: string,
): Promise<ApiResult<Record<string, number>>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('time_entries')
    .select('user_id, hours')
    .eq('project_id', projectId);
  if (error) return err(error.message);
  const out: Record<string, number> = {};
  for (const r of (data ?? []) as Array<{ user_id: string; hours: number | string }>) {
    if (!r.user_id) continue;
    out[r.user_id] = (out[r.user_id] ?? 0) + Number(r.hours);
  }
  return ok(out);
}

// ===========================================================================
// Phase 30.27: Boards + Work Items
// ===========================================================================

interface BoardRow {
  id: string;
  organization_id: string;
  project_id: string;
  name: string;
  description: string | null;
  sort_order: number | string;
  is_archived: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

function fromBoardRow(r: BoardRow): Board {
  return {
    id: r.id,
    organizationId: r.organization_id,
    projectId: r.project_id,
    name: r.name,
    description: r.description ?? undefined,
    sortOrder: Number(r.sort_order),
    isArchived: r.is_archived,
    createdBy: r.created_by ?? undefined,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export async function listBoards(projectId: string): Promise<ApiResult<Board[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('boards')
    .select('*')
    .eq('project_id', projectId)
    .eq('is_archived', false)
    .order('sort_order', { ascending: true });
  if (error) return err(error.message);
  return ok((data as BoardRow[]).map(fromBoardRow));
}

export async function createBoard(
  orgId: string,
  projectId: string,
  name: string,
  description?: string,
): Promise<ApiResult<Board>> {
  if (!supabase) return err(NOT_AUTH);
  const { data: userData } = await supabase.auth.getUser();
  const { data, error } = await supabase
    .from('boards')
    .insert({
      organization_id: orgId,
      project_id: projectId,
      name,
      description: description ?? null,
      sort_order: Date.now(), // simple monotonic default; user can reorder
      created_by: userData?.user?.id ?? null,
    })
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Create failed');
  return ok(fromBoardRow(data as BoardRow));
}

export async function updateBoard(
  id: string,
  patch: { name?: string; description?: string | null; sortOrder?: number; isArchived?: boolean },
): Promise<ApiResult<Board>> {
  if (!supabase) return err(NOT_AUTH);
  const row: Record<string, unknown> = {};
  if (patch.name !== undefined) row.name = patch.name;
  if (patch.description !== undefined) row.description = patch.description;
  if (patch.sortOrder !== undefined) row.sort_order = patch.sortOrder;
  if (patch.isArchived !== undefined) row.is_archived = patch.isArchived;
  const { data, error } = await supabase
    .from('boards')
    .update(row)
    .eq('id', id)
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Update failed');
  return ok(fromBoardRow(data as BoardRow));
}

export async function deleteBoard(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('boards').delete().eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

// ---------- Board Columns (workspace statuses) ----------

interface BoardColumnRow {
  id: string;
  organization_id: string;
  name: string;
  color: string | null;
  sort_order: number | string;
  is_terminal: boolean;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
}

function fromBoardColumnRow(r: BoardColumnRow): BoardColumn {
  return {
    id: r.id,
    organizationId: r.organization_id,
    name: r.name,
    color: r.color ?? undefined,
    sortOrder: Number(r.sort_order),
    isTerminal: r.is_terminal,
    isArchived: r.is_archived,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export async function listBoardColumns(orgId: string): Promise<ApiResult<BoardColumn[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('board_columns')
    .select('*')
    .eq('organization_id', orgId)
    .order('sort_order', { ascending: true });
  if (error) return err(error.message);
  return ok((data as BoardColumnRow[]).map(fromBoardColumnRow));
}

export async function createBoardColumn(
  orgId: string,
  data: { name: string; color?: string; sortOrder?: number; isTerminal?: boolean },
): Promise<ApiResult<BoardColumn>> {
  if (!supabase) return err(NOT_AUTH);
  const { data: row, error } = await supabase
    .from('board_columns')
    .insert({
      organization_id: orgId,
      name: data.name,
      color: data.color ?? null,
      sort_order: data.sortOrder ?? Date.now(),
      is_terminal: data.isTerminal ?? false,
    })
    .select()
    .single();
  if (error || !row) return err(error?.message ?? 'Create failed');
  return ok(fromBoardColumnRow(row as BoardColumnRow));
}

export async function updateBoardColumn(
  id: string,
  patch: {
    name?: string;
    color?: string | null;
    sortOrder?: number;
    isTerminal?: boolean;
    isArchived?: boolean;
  },
): Promise<ApiResult<BoardColumn>> {
  if (!supabase) return err(NOT_AUTH);
  const row: Record<string, unknown> = {};
  if (patch.name !== undefined) row.name = patch.name;
  if (patch.color !== undefined) row.color = patch.color;
  if (patch.sortOrder !== undefined) row.sort_order = patch.sortOrder;
  if (patch.isTerminal !== undefined) row.is_terminal = patch.isTerminal;
  if (patch.isArchived !== undefined) row.is_archived = patch.isArchived;
  const { data, error } = await supabase
    .from('board_columns')
    .update(row)
    .eq('id', id)
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Update failed');
  return ok(fromBoardColumnRow(data as BoardColumnRow));
}

export async function deleteBoardColumn(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('board_columns').delete().eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

// ---------- Work Item Types ----------

interface WorkItemTypeRow {
  id: string;
  organization_id: string;
  name: string;
  icon: string | null;
  color: string | null;
  sort_order: number | string;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
}

function fromWorkItemTypeRow(r: WorkItemTypeRow): WorkItemType {
  return {
    id: r.id,
    organizationId: r.organization_id,
    name: r.name,
    icon: r.icon ?? undefined,
    color: r.color ?? undefined,
    sortOrder: Number(r.sort_order),
    isArchived: r.is_archived,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export async function listWorkItemTypes(orgId: string): Promise<ApiResult<WorkItemType[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('work_item_types')
    .select('*')
    .eq('organization_id', orgId)
    .order('sort_order', { ascending: true });
  if (error) return err(error.message);
  return ok((data as WorkItemTypeRow[]).map(fromWorkItemTypeRow));
}

export async function createWorkItemType(
  orgId: string,
  data: { name: string; icon?: string; color?: string; sortOrder?: number },
): Promise<ApiResult<WorkItemType>> {
  if (!supabase) return err(NOT_AUTH);
  const { data: row, error } = await supabase
    .from('work_item_types')
    .insert({
      organization_id: orgId,
      name: data.name,
      icon: data.icon ?? null,
      color: data.color ?? null,
      sort_order: data.sortOrder ?? Date.now(),
    })
    .select()
    .single();
  if (error || !row) return err(error?.message ?? 'Create failed');
  return ok(fromWorkItemTypeRow(row as WorkItemTypeRow));
}

export async function updateWorkItemType(
  id: string,
  patch: {
    name?: string;
    icon?: string | null;
    color?: string | null;
    sortOrder?: number;
    isArchived?: boolean;
  },
): Promise<ApiResult<WorkItemType>> {
  if (!supabase) return err(NOT_AUTH);
  const row: Record<string, unknown> = {};
  if (patch.name !== undefined) row.name = patch.name;
  if (patch.icon !== undefined) row.icon = patch.icon;
  if (patch.color !== undefined) row.color = patch.color;
  if (patch.sortOrder !== undefined) row.sort_order = patch.sortOrder;
  if (patch.isArchived !== undefined) row.is_archived = patch.isArchived;
  const { data, error } = await supabase
    .from('work_item_types')
    .update(row)
    .eq('id', id)
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Update failed');
  return ok(fromWorkItemTypeRow(data as WorkItemTypeRow));
}

export async function deleteWorkItemType(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('work_item_types').delete().eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

// ---------- Work Items ----------

interface WorkItemRow {
  id: string;
  organization_id: string;
  board_id: string;
  project_id: string;
  task_id: string | null;
  /** Phase 30.29: nullable sprint FK; null = backlog */
  sprint_id: string | null;
  title: string;
  description: string | null;
  type_id: string | null;
  status_id: string | null;
  assignee_user_id: string | null;
  due_date: string | null;
  story_points: number | string | null;
  sort_order: number | string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

function fromWorkItemRow(r: WorkItemRow): WorkItem {
  return {
    id: r.id,
    organizationId: r.organization_id,
    boardId: r.board_id,
    projectId: r.project_id,
    taskId: r.task_id,
    sprintId: r.sprint_id ?? null,
    title: r.title,
    description: r.description ?? undefined,
    typeId: r.type_id,
    statusId: r.status_id,
    assigneeUserId: r.assignee_user_id,
    dueDate: r.due_date,
    storyPoints: r.story_points !== null ? Number(r.story_points) : null,
    sortOrder: Number(r.sort_order),
    createdBy: r.created_by ?? undefined,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export async function listWorkItems(boardId: string): Promise<ApiResult<WorkItem[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('work_items')
    .select('*')
    .eq('board_id', boardId)
    .order('sort_order', { ascending: true });
  if (error) return err(error.message);
  return ok((data as WorkItemRow[]).map(fromWorkItemRow));
}

/**
 * Phase 30.28: fetch all work items across all boards for a project. Used
 * by ScheduleTab to compute per-task linked-item counts and % nudge.
 */
export async function listWorkItemsByProject(projectId: string): Promise<ApiResult<WorkItem[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('work_items')
    .select('*')
    .eq('project_id', projectId);
  if (error) return err(error.message);
  return ok((data as WorkItemRow[]).map(fromWorkItemRow));
}

export async function createWorkItem(
  orgId: string,
  boardId: string,
  projectId: string,
  data: {
    title: string;
    description?: string;
    typeId?: string | null;
    statusId?: string | null;
    assigneeUserId?: string | null;
    dueDate?: string | null;
    storyPoints?: number | null;
    taskId?: string | null;
    sprintId?: string | null;
    sortOrder?: number;
  },
): Promise<ApiResult<WorkItem>> {
  if (!supabase) return err(NOT_AUTH);
  const { data: userData } = await supabase.auth.getUser();
  const row = {
    organization_id: orgId,
    board_id: boardId,
    project_id: projectId,
    title: data.title,
    description: data.description ?? null,
    type_id: data.typeId ?? null,
    status_id: data.statusId ?? null,
    assignee_user_id: data.assigneeUserId ?? null,
    due_date: data.dueDate ?? null,
    story_points: data.storyPoints ?? null,
    task_id: data.taskId ?? null,
    sprint_id: data.sprintId ?? null,
    sort_order: data.sortOrder ?? Date.now(),
    created_by: userData?.user?.id ?? null,
  };
  const { data: inserted, error } = await supabase
    .from('work_items')
    .insert(row)
    .select()
    .single();
  if (error || !inserted) return err(error?.message ?? 'Create failed');
  return ok(fromWorkItemRow(inserted as WorkItemRow));
}

export async function updateWorkItem(
  id: string,
  patch: {
    title?: string;
    description?: string | null;
    typeId?: string | null;
    statusId?: string | null;
    assigneeUserId?: string | null;
    dueDate?: string | null;
    storyPoints?: number | null;
    taskId?: string | null;
    sprintId?: string | null;
    sortOrder?: number;
  },
): Promise<ApiResult<WorkItem>> {
  if (!supabase) return err(NOT_AUTH);
  const row: Record<string, unknown> = {};
  if (patch.title !== undefined) row.title = patch.title;
  if (patch.description !== undefined) row.description = patch.description;
  if (patch.typeId !== undefined) row.type_id = patch.typeId;
  if (patch.statusId !== undefined) row.status_id = patch.statusId;
  if (patch.assigneeUserId !== undefined) row.assignee_user_id = patch.assigneeUserId;
  if (patch.dueDate !== undefined) row.due_date = patch.dueDate;
  if (patch.storyPoints !== undefined) row.story_points = patch.storyPoints;
  if (patch.taskId !== undefined) row.task_id = patch.taskId;
  if (patch.sprintId !== undefined) row.sprint_id = patch.sprintId;
  if (patch.sortOrder !== undefined) row.sort_order = patch.sortOrder;
  const { data, error } = await supabase
    .from('work_items')
    .update(row)
    .eq('id', id)
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Update failed');
  return ok(fromWorkItemRow(data as WorkItemRow));
}

export async function deleteWorkItem(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('work_items').delete().eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

// ===========================================================================
// Phase 30.29: Sprints
// ===========================================================================

interface SprintRow {
  id: string;
  organization_id: string;
  board_id: string;
  name: string;
  goal: string | null;
  start_date: string | null;
  end_date: string | null;
  state: string;
  completed_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

function fromSprintRow(r: SprintRow): Sprint {
  return {
    id: r.id,
    organizationId: r.organization_id,
    boardId: r.board_id,
    name: r.name,
    goal: r.goal ?? undefined,
    startDate: r.start_date,
    endDate: r.end_date,
    state: r.state as Sprint['state'],
    completedAt: r.completed_at,
    createdBy: r.created_by ?? undefined,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export async function listSprints(boardId: string): Promise<ApiResult<Sprint[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('sprints')
    .select('*')
    .eq('board_id', boardId)
    .order('start_date', { ascending: false, nullsFirst: false })
    .order('created_at', { ascending: false });
  if (error) return err(error.message);
  return ok((data as SprintRow[]).map(fromSprintRow));
}

export async function createSprint(
  orgId: string,
  boardId: string,
  data: {
    name: string;
    goal?: string;
    startDate?: string | null;
    endDate?: string | null;
    state?: Sprint['state'];
  },
): Promise<ApiResult<Sprint>> {
  if (!supabase) return err(NOT_AUTH);
  const { data: userData } = await supabase.auth.getUser();
  const { data: row, error } = await supabase
    .from('sprints')
    .insert({
      organization_id: orgId,
      board_id: boardId,
      name: data.name,
      goal: data.goal ?? null,
      start_date: data.startDate ?? null,
      end_date: data.endDate ?? null,
      state: data.state ?? 'planning',
      created_by: userData?.user?.id ?? null,
    })
    .select()
    .single();
  if (error || !row) return err(error?.message ?? 'Create failed');
  return ok(fromSprintRow(row as SprintRow));
}

export async function updateSprint(
  id: string,
  patch: {
    name?: string;
    goal?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    state?: Sprint['state'];
  },
): Promise<ApiResult<Sprint>> {
  if (!supabase) return err(NOT_AUTH);
  const row: Record<string, unknown> = {};
  if (patch.name !== undefined) row.name = patch.name;
  if (patch.goal !== undefined) row.goal = patch.goal;
  if (patch.startDate !== undefined) row.start_date = patch.startDate;
  if (patch.endDate !== undefined) row.end_date = patch.endDate;
  if (patch.state !== undefined) {
    row.state = patch.state;
    // Stamp completed_at when transitioning to completed
    if (patch.state === 'completed') row.completed_at = new Date().toISOString();
  }
  const { data, error } = await supabase
    .from('sprints')
    .update(row)
    .eq('id', id)
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Update failed');
  return ok(fromSprintRow(data as SprintRow));
}

export async function deleteSprint(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('sprints').delete().eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

/**
 * Phase 30.29: bulk-move all incomplete items from a sprint to a
 * destination. If targetSprintId is null, they go to the backlog.
 * Used by the sprint completion flow. terminalStatusIds identifies which
 * items count as "complete" and should NOT be moved.
 */
export async function moveIncompleteSprintItems(
  sprintId: string,
  terminalStatusIds: string[],
  targetSprintId: string | null,
): Promise<ApiResult<number>> {
  if (!supabase) return err(NOT_AUTH);
  // Fetch all items in the sprint, filter client-side. Sprints rarely
  // have >100 items so client-side filtering is fine and simpler than
  // wrestling with PostgREST NOT IN syntax.
  const { data: allItems, error: fetchErr } = await supabase
    .from('work_items')
    .select('id, status_id')
    .eq('sprint_id', sprintId);
  if (fetchErr) return err(fetchErr.message);

  const terminal = new Set(terminalStatusIds);
  const incompleteIds = (allItems ?? [])
    .filter((w: { status_id: string | null }) => !w.status_id || !terminal.has(w.status_id))
    .map((w: { id: string }) => w.id);

  if (incompleteIds.length === 0) return ok(0);

  const { error: updateErr } = await supabase
    .from('work_items')
    .update({ sprint_id: targetSprintId })
    .in('id', incompleteIds);
  if (updateErr) return err(updateErr.message);
  return ok(incompleteIds.length);
}

// ===========================================================================
// Phase 30.30: Sprint burndown snapshots
// ===========================================================================

interface SprintBurndownSnapshotRow {
  id: string;
  sprint_id: string;
  snapshot_date: string;
  total_items: number | string;
  done_items: number | string;
  total_points: number | string;
  remaining_points: number | string;
  created_at: string;
  updated_at: string;
}

function fromSnapshotRow(r: SprintBurndownSnapshotRow): SprintBurndownSnapshot {
  return {
    id: r.id,
    sprintId: r.sprint_id,
    snapshotDate: r.snapshot_date,
    totalItems: Number(r.total_items),
    doneItems: Number(r.done_items),
    totalPoints: Number(r.total_points),
    remainingPoints: Number(r.remaining_points),
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

/** List all snapshots for a sprint, ordered by date ascending. */
export async function listSprintSnapshots(sprintId: string): Promise<ApiResult<SprintBurndownSnapshot[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('sprint_burndown_snapshots')
    .select('*')
    .eq('sprint_id', sprintId)
    .order('snapshot_date', { ascending: true });
  if (error) return err(error.message);
  return ok((data as SprintBurndownSnapshotRow[]).map(fromSnapshotRow));
}

/**
 * Batch-fetch snapshots for multiple sprints. Used by velocity chart to
 * show trailing N sprints at once. Returns a map keyed by sprintId.
 */
export async function listSprintSnapshotsBatch(sprintIds: string[]): Promise<ApiResult<Record<string, SprintBurndownSnapshot[]>>> {
  if (!supabase) return err(NOT_AUTH);
  if (sprintIds.length === 0) return ok({});
  const { data, error } = await supabase
    .from('sprint_burndown_snapshots')
    .select('*')
    .in('sprint_id', sprintIds)
    .order('snapshot_date', { ascending: true });
  if (error) return err(error.message);
  const map: Record<string, SprintBurndownSnapshot[]> = {};
  for (const row of (data as SprintBurndownSnapshotRow[])) {
    const s = fromSnapshotRow(row);
    if (!map[s.sprintId]) map[s.sprintId] = [];
    map[s.sprintId].push(s);
  }
  return ok(map);
}

/**
 * Upsert a snapshot for today. If a row already exists for
 * (sprint_id, snapshot_date=today), overwrite its metrics.
 */
export async function upsertSprintSnapshot(
  sprintId: string,
  metrics: { totalItems: number; doneItems: number; totalPoints: number; remainingPoints: number },
): Promise<ApiResult<SprintBurndownSnapshot>> {
  if (!supabase) return err(NOT_AUTH);
  const today = new Date().toISOString().split('T')[0];
  const { data, error } = await supabase
    .from('sprint_burndown_snapshots')
    .upsert(
      {
        sprint_id: sprintId,
        snapshot_date: today,
        total_items: metrics.totalItems,
        done_items: metrics.doneItems,
        total_points: metrics.totalPoints,
        remaining_points: metrics.remainingPoints,
      },
      { onConflict: 'sprint_id,snapshot_date' },
    )
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Upsert failed');
  return ok(fromSnapshotRow(data as SprintBurndownSnapshotRow));
}

// ===========================================================================
// Phase 30.33: Working calendar + exceptions
// ===========================================================================

interface WorkingCalendarRow {
  id: string;
  organization_id: string;
  name: string;
  working_days_bitmask: number | string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

function fromWorkingCalendarRow(r: WorkingCalendarRow): WorkingCalendar {
  return {
    id: r.id,
    organizationId: r.organization_id,
    name: r.name,
    workingDaysBitmask: Number(r.working_days_bitmask),
    isDefault: r.is_default,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

interface WorkingCalendarExceptionRow {
  id: string;
  calendar_id: string;
  organization_id: string;
  name: string;
  description: string | null;
  date_from: string;
  date_to: string;
  exception_type: string;
  is_working: boolean;
  created_at: string;
  updated_at: string;
}

function fromExceptionRow(r: WorkingCalendarExceptionRow): WorkingCalendarException {
  return {
    id: r.id,
    calendarId: r.calendar_id,
    organizationId: r.organization_id,
    name: r.name,
    description: r.description ?? undefined,
    dateFrom: r.date_from,
    dateTo: r.date_to,
    exceptionType: r.exception_type as WorkingCalendarException['exceptionType'],
    isWorking: r.is_working,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

/**
 * Get the default calendar for the workspace. If none exists (edge case
 * for freshly-created orgs where the seed trigger hasn't yet fired), a
 * best-effort default is created.
 */
export async function getDefaultCalendar(orgId: string): Promise<ApiResult<WorkingCalendar>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('working_calendars')
    .select('*')
    .eq('organization_id', orgId)
    .eq('is_default', true)
    .maybeSingle();
  if (error) return err(error.message);
  if (data) return ok(fromWorkingCalendarRow(data as WorkingCalendarRow));
  // No default calendar yet — create one
  const { data: created, error: createErr } = await supabase
    .from('working_calendars')
    .insert({ organization_id: orgId, name: 'Default Calendar', working_days_bitmask: 62, is_default: true })
    .select()
    .single();
  if (createErr || !created) return err(createErr?.message ?? 'Calendar create failed');
  return ok(fromWorkingCalendarRow(created as WorkingCalendarRow));
}

export async function updateWorkingCalendar(
  id: string,
  patch: { name?: string; workingDaysBitmask?: number },
): Promise<ApiResult<WorkingCalendar>> {
  if (!supabase) return err(NOT_AUTH);
  const row: Record<string, unknown> = {};
  if (patch.name !== undefined) row.name = patch.name;
  if (patch.workingDaysBitmask !== undefined) row.working_days_bitmask = patch.workingDaysBitmask;
  const { data, error } = await supabase
    .from('working_calendars')
    .update(row)
    .eq('id', id)
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Update failed');
  return ok(fromWorkingCalendarRow(data as WorkingCalendarRow));
}

export async function listCalendarExceptions(calendarId: string): Promise<ApiResult<WorkingCalendarException[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('working_calendar_exceptions')
    .select('*')
    .eq('calendar_id', calendarId)
    .order('date_from', { ascending: true });
  if (error) return err(error.message);
  return ok((data as WorkingCalendarExceptionRow[]).map(fromExceptionRow));
}

export async function createCalendarException(
  orgId: string,
  calendarId: string,
  data: {
    name: string;
    description?: string;
    dateFrom: string;
    dateTo: string;
    exceptionType: WorkingCalendarException['exceptionType'];
    isWorking: boolean;
  },
): Promise<ApiResult<WorkingCalendarException>> {
  if (!supabase) return err(NOT_AUTH);
  const { data: row, error } = await supabase
    .from('working_calendar_exceptions')
    .insert({
      organization_id: orgId,
      calendar_id: calendarId,
      name: data.name,
      description: data.description ?? null,
      date_from: data.dateFrom,
      date_to: data.dateTo,
      exception_type: data.exceptionType,
      is_working: data.isWorking,
    })
    .select()
    .single();
  if (error || !row) return err(error?.message ?? 'Create failed');
  return ok(fromExceptionRow(row as WorkingCalendarExceptionRow));
}

export async function updateCalendarException(
  id: string,
  patch: {
    name?: string;
    description?: string | null;
    dateFrom?: string;
    dateTo?: string;
    exceptionType?: WorkingCalendarException['exceptionType'];
    isWorking?: boolean;
  },
): Promise<ApiResult<WorkingCalendarException>> {
  if (!supabase) return err(NOT_AUTH);
  const row: Record<string, unknown> = {};
  if (patch.name !== undefined) row.name = patch.name;
  if (patch.description !== undefined) row.description = patch.description;
  if (patch.dateFrom !== undefined) row.date_from = patch.dateFrom;
  if (patch.dateTo !== undefined) row.date_to = patch.dateTo;
  if (patch.exceptionType !== undefined) row.exception_type = patch.exceptionType;
  if (patch.isWorking !== undefined) row.is_working = patch.isWorking;
  const { data, error } = await supabase
    .from('working_calendar_exceptions')
    .update(row)
    .eq('id', id)
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Update failed');
  return ok(fromExceptionRow(data as WorkingCalendarExceptionRow));
}

export async function deleteCalendarException(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('working_calendar_exceptions').delete().eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

// ===========================================================================
// Phase 30.35: Insight snoozes
// ===========================================================================

interface InsightSnoozeRow {
  id: string;
  user_id: string;
  organization_id: string;
  project_id: string | null;
  rule_id: string;
  target_kind: string;
  target_id: string;
  snoozed_until: string;
  created_at: string;
}

function fromSnoozeRow(r: InsightSnoozeRow): InsightSnooze {
  return {
    id: r.id,
    userId: r.user_id,
    organizationId: r.organization_id,
    projectId: r.project_id ?? undefined,
    ruleId: r.rule_id,
    targetKind: r.target_kind as InsightTargetKind,
    targetId: r.target_id,
    snoozedUntil: r.snoozed_until,
    createdAt: r.created_at,
  };
}

export async function listInsightSnoozes(orgId: string): Promise<ApiResult<InsightSnooze[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('insight_snoozes')
    .select('*')
    .eq('organization_id', orgId);
  if (error) return err(error.message);
  return ok((data as InsightSnoozeRow[]).map(fromSnoozeRow));
}

/**
 * Upsert a snooze. If one exists for (user, rule, target, project), extend
 * its snoozed_until — otherwise create a new row.
 */
export async function upsertInsightSnooze(
  orgId: string,
  projectId: string | null,
  ruleId: string,
  targetKind: InsightTargetKind,
  targetId: string,
  snoozedUntil: string,
): Promise<ApiResult<InsightSnooze>> {
  if (!supabase) return err(NOT_AUTH);
  const { data: user } = await supabase.auth.getUser();
  const userId = user?.user?.id;
  if (!userId) return err('Not authenticated');

  // Try find existing
  let query = supabase
    .from('insight_snoozes')
    .select('*')
    .eq('user_id', userId)
    .eq('organization_id', orgId)
    .eq('rule_id', ruleId)
    .eq('target_kind', targetKind)
    .eq('target_id', targetId);
  if (projectId) query = query.eq('project_id', projectId);
  else query = query.is('project_id', null);
  const { data: existing } = await query.maybeSingle();

  if (existing) {
    const { data, error } = await supabase
      .from('insight_snoozes')
      .update({ snoozed_until: snoozedUntil })
      .eq('id', (existing as InsightSnoozeRow).id)
      .select()
      .single();
    if (error || !data) return err(error?.message ?? 'Snooze update failed');
    return ok(fromSnoozeRow(data as InsightSnoozeRow));
  }
  const { data, error } = await supabase
    .from('insight_snoozes')
    .insert({
      user_id: userId,
      organization_id: orgId,
      project_id: projectId,
      rule_id: ruleId,
      target_kind: targetKind,
      target_id: targetId,
      snoozed_until: snoozedUntil,
    })
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Snooze insert failed');
  return ok(fromSnoozeRow(data as InsightSnoozeRow));
}

export async function deleteInsightSnooze(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('insight_snoozes').delete().eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

// ===========================================================================
// Phase 31: Requirements + test cases
// ===========================================================================

interface RequirementRow {
  id: string;
  project_id: string;
  organization_id: string;
  req_code: string;
  title: string;
  description: string | null;
  category: string;
  status: string;
  priority: string;
  source: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

function fromRequirementRow(r: RequirementRow): Requirement {
  return {
    id: r.id,
    projectId: r.project_id,
    organizationId: r.organization_id,
    reqCode: r.req_code,
    title: r.title,
    description: r.description ?? undefined,
    category: r.category as RequirementCategory,
    status: r.status as RequirementStatus,
    priority: r.priority as RequirementPriority,
    source: r.source ?? undefined,
    createdBy: r.created_by ?? undefined,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

interface RequirementTestCaseRow {
  id: string;
  requirement_id: string;
  organization_id: string;
  test_code: string | null;
  test_name: string;
  test_steps: string | null;
  expected_result: string | null;
  actual_result: string | null;
  test_status: string;
  tested_by: string | null;
  tested_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

function fromTestCaseRow(r: RequirementTestCaseRow): RequirementTestCase {
  return {
    id: r.id,
    requirementId: r.requirement_id,
    organizationId: r.organization_id,
    testCode: r.test_code ?? undefined,
    testName: r.test_name,
    testSteps: r.test_steps ?? undefined,
    expectedResult: r.expected_result ?? undefined,
    actualResult: r.actual_result ?? undefined,
    testStatus: r.test_status as TestCaseStatus,
    testedBy: r.tested_by ?? undefined,
    testedAt: r.tested_at ?? undefined,
    notes: r.notes ?? undefined,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export async function listRequirements(projectId: string): Promise<ApiResult<Requirement[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('requirements')
    .select('*')
    .eq('project_id', projectId)
    .order('req_code', { ascending: true });
  if (error) return err(error.message);
  return ok((data as RequirementRow[]).map(fromRequirementRow));
}

export async function createRequirement(
  orgId: string,
  projectId: string,
  data: {
    reqCode: string;
    title: string;
    description?: string;
    category: RequirementCategory;
    status?: RequirementStatus;
    priority?: RequirementPriority;
    source?: string;
  },
): Promise<ApiResult<Requirement>> {
  if (!supabase) return err(NOT_AUTH);
  const { data: user } = await supabase.auth.getUser();
  const { data: row, error } = await supabase
    .from('requirements')
    .insert({
      organization_id: orgId,
      project_id: projectId,
      req_code: data.reqCode,
      title: data.title,
      description: data.description ?? null,
      category: data.category,
      status: data.status ?? 'draft',
      priority: data.priority ?? 'medium',
      source: data.source ?? null,
      created_by: user?.user?.id ?? null,
    })
    .select()
    .single();
  if (error || !row) return err(error?.message ?? 'Create failed');
  return ok(fromRequirementRow(row as RequirementRow));
}

export async function updateRequirement(
  id: string,
  patch: Partial<{
    reqCode: string;
    title: string;
    description: string | null;
    category: RequirementCategory;
    status: RequirementStatus;
    priority: RequirementPriority;
    source: string | null;
  }>,
): Promise<ApiResult<Requirement>> {
  if (!supabase) return err(NOT_AUTH);
  const row: Record<string, unknown> = {};
  if (patch.reqCode !== undefined) row.req_code = patch.reqCode;
  if (patch.title !== undefined) row.title = patch.title;
  if (patch.description !== undefined) row.description = patch.description;
  if (patch.category !== undefined) row.category = patch.category;
  if (patch.status !== undefined) row.status = patch.status;
  if (patch.priority !== undefined) row.priority = patch.priority;
  if (patch.source !== undefined) row.source = patch.source;
  const { data, error } = await supabase
    .from('requirements')
    .update(row)
    .eq('id', id)
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Update failed');
  return ok(fromRequirementRow(data as RequirementRow));
}

export async function deleteRequirement(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('requirements').delete().eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

// ---------------------------------------------------------------------------
// Benefits Realization (paid module — Pro/Enterprise)
// ---------------------------------------------------------------------------

interface BenefitRow {
  id: string;
  project_id: string;
  organization_id: string;
  name: string;
  description: string | null;
  category: string;
  owner: string | null;
  unit: string | null;
  baseline_value: number | null;
  target_value: number | null;
  actual_value: number | null;
  higher_is_better: boolean | null;
  target_date: string | null;
  realized_date: string | null;
  status: string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

function fromBenefitRow(r: BenefitRow): Benefit {
  return {
    id: r.id,
    projectId: r.project_id,
    organizationId: r.organization_id,
    name: r.name,
    description: r.description ?? undefined,
    category: r.category as BenefitCategory,
    owner: r.owner ?? undefined,
    unit: r.unit ?? undefined,
    baselineValue: r.baseline_value ?? undefined,
    targetValue: r.target_value ?? undefined,
    actualValue: r.actual_value ?? undefined,
    higherIsBetter: r.higher_is_better ?? undefined,
    targetDate: r.target_date ?? undefined,
    realizedDate: r.realized_date ?? undefined,
    status: r.status as BenefitStatus,
    createdBy: r.created_by ?? undefined,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export async function listBenefits(projectId: string): Promise<ApiResult<Benefit[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('benefits')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: true });
  if (error) return err(error.message);
  return ok((data as BenefitRow[]).map(fromBenefitRow));
}

export async function createBenefit(
  orgId: string,
  projectId: string,
  data: {
    name: string;
    description?: string;
    category: BenefitCategory;
    owner?: string;
    unit?: string;
    baselineValue?: number;
    targetValue?: number;
    actualValue?: number;
    higherIsBetter?: boolean;
    targetDate?: string;
    realizedDate?: string;
    status?: BenefitStatus;
  },
): Promise<ApiResult<Benefit>> {
  if (!supabase) return err(NOT_AUTH);
  const { data: user } = await supabase.auth.getUser();
  const { data: row, error } = await supabase
    .from('benefits')
    .insert({
      organization_id: orgId,
      project_id: projectId,
      name: data.name,
      description: data.description ?? null,
      category: data.category,
      owner: data.owner ?? null,
      unit: data.unit ?? null,
      baseline_value: data.baselineValue ?? null,
      target_value: data.targetValue ?? null,
      actual_value: data.actualValue ?? null,
      higher_is_better: data.higherIsBetter ?? null,
      target_date: data.targetDate ?? null,
      realized_date: data.realizedDate ?? null,
      status: data.status ?? 'planned',
      created_by: user?.user?.id ?? null,
    })
    .select()
    .single();
  if (error || !row) return err(error?.message ?? 'Create failed');
  return ok(fromBenefitRow(row as BenefitRow));
}

export async function updateBenefit(
  id: string,
  patch: Partial<{
    name: string;
    description: string | null;
    category: BenefitCategory;
    owner: string | null;
    unit: string | null;
    baselineValue: number | null;
    targetValue: number | null;
    actualValue: number | null;
    higherIsBetter: boolean | null;
    targetDate: string | null;
    realizedDate: string | null;
    status: BenefitStatus;
  }>,
): Promise<ApiResult<Benefit>> {
  if (!supabase) return err(NOT_AUTH);
  const row: Record<string, unknown> = {};
  if (patch.name !== undefined) row.name = patch.name;
  if (patch.description !== undefined) row.description = patch.description;
  if (patch.category !== undefined) row.category = patch.category;
  if (patch.owner !== undefined) row.owner = patch.owner;
  if (patch.unit !== undefined) row.unit = patch.unit;
  if (patch.baselineValue !== undefined) row.baseline_value = patch.baselineValue;
  if (patch.targetValue !== undefined) row.target_value = patch.targetValue;
  if (patch.actualValue !== undefined) row.actual_value = patch.actualValue;
  if (patch.higherIsBetter !== undefined) row.higher_is_better = patch.higherIsBetter;
  if (patch.targetDate !== undefined) row.target_date = patch.targetDate;
  if (patch.realizedDate !== undefined) row.realized_date = patch.realizedDate;
  if (patch.status !== undefined) row.status = patch.status;
  const { data, error } = await supabase
    .from('benefits')
    .update(row)
    .eq('id', id)
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Update failed');
  return ok(fromBenefitRow(data as BenefitRow));
}

export async function deleteBenefit(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('benefits').delete().eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

// ---------------------------------------------------------------------------
// Intake & Demand Management (paid module — Pro/Enterprise)
// ---------------------------------------------------------------------------

interface DemandRow {
  id: string;
  organization_id: string;
  title: string;
  description: string | null;
  requester: string | null;
  type: string | null;
  estimated_cost: number | null;
  estimated_value: number | null;
  target_date: string | null;
  score_alignment: number | null;
  score_value: number | null;
  score_urgency: number | null;
  score_risk: number | null;
  score_effort: number | null;
  stage: string;
  decision_notes: string | null;
  size_tier_id: string | null;
  revisit_date: string | null;
  pipeline_project_id: string | null;
  converted_project_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

function fromDemandRow(r: DemandRow): Demand {
  return {
    id: r.id,
    organizationId: r.organization_id,
    title: r.title,
    description: r.description ?? undefined,
    requester: r.requester ?? undefined,
    type: r.type ?? undefined,
    estimatedCost: r.estimated_cost ?? undefined,
    estimatedValue: r.estimated_value ?? undefined,
    targetDate: r.target_date ?? undefined,
    scoreAlignment: r.score_alignment ?? undefined,
    scoreValue: r.score_value ?? undefined,
    scoreUrgency: r.score_urgency ?? undefined,
    scoreRisk: r.score_risk ?? undefined,
    scoreEffort: r.score_effort ?? undefined,
    stage: r.stage as DemandStage,
    decisionNotes: r.decision_notes ?? undefined,
    sizeTierId: r.size_tier_id ?? null,
    revisitDate: r.revisit_date ?? undefined,
    pipelineProjectId: r.pipeline_project_id ?? undefined,
    convertedProjectId: r.converted_project_id ?? undefined,
    createdBy: r.created_by ?? undefined,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export interface DemandInput {
  title: string;
  description?: string;
  requester?: string;
  type?: string;
  estimatedCost?: number;
  estimatedValue?: number;
  targetDate?: string;
  scoreAlignment?: number;
  scoreValue?: number;
  scoreUrgency?: number;
  scoreRisk?: number;
  scoreEffort?: number;
  stage?: DemandStage;
  decisionNotes?: string;
  sizeTierId?: string | null;
  revisitDate?: string;
}

export async function listDemands(orgId: string): Promise<ApiResult<Demand[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('demands')
    .select('*')
    .eq('organization_id', orgId)
    .order('created_at', { ascending: false });
  if (error) return err(error.message);
  return ok((data as DemandRow[]).map(fromDemandRow));
}

export async function createDemand(orgId: string, data: DemandInput): Promise<ApiResult<Demand>> {
  if (!supabase) return err(NOT_AUTH);
  const { data: user } = await supabase.auth.getUser();
  const { data: row, error } = await supabase
    .from('demands')
    .insert({
      organization_id: orgId,
      title: data.title,
      description: data.description ?? null,
      requester: data.requester ?? null,
      type: data.type ?? null,
      estimated_cost: data.estimatedCost ?? null,
      estimated_value: data.estimatedValue ?? null,
      target_date: data.targetDate ?? null,
      score_alignment: data.scoreAlignment ?? null,
      score_value: data.scoreValue ?? null,
      score_urgency: data.scoreUrgency ?? null,
      score_risk: data.scoreRisk ?? null,
      score_effort: data.scoreEffort ?? null,
      stage: data.stage ?? 'new',
      decision_notes: data.decisionNotes ?? null,
      size_tier_id: data.sizeTierId ?? null,
      revisit_date: data.revisitDate ?? null,
      created_by: user?.user?.id ?? null,
    })
    .select()
    .single();
  if (error || !row) return err(error?.message ?? 'Create failed');
  return ok(fromDemandRow(row as DemandRow));
}

export async function updateDemand(
  id: string,
  patch: Partial<DemandInput & { convertedProjectId: string | null; pipelineProjectId: string | null }>,
): Promise<ApiResult<Demand>> {
  if (!supabase) return err(NOT_AUTH);
  const row: Record<string, unknown> = {};
  if (patch.title !== undefined) row.title = patch.title;
  if (patch.description !== undefined) row.description = patch.description;
  if (patch.requester !== undefined) row.requester = patch.requester;
  if (patch.type !== undefined) row.type = patch.type;
  if (patch.estimatedCost !== undefined) row.estimated_cost = patch.estimatedCost;
  if (patch.estimatedValue !== undefined) row.estimated_value = patch.estimatedValue;
  if (patch.targetDate !== undefined) row.target_date = patch.targetDate;
  if (patch.scoreAlignment !== undefined) row.score_alignment = patch.scoreAlignment;
  if (patch.scoreValue !== undefined) row.score_value = patch.scoreValue;
  if (patch.scoreUrgency !== undefined) row.score_urgency = patch.scoreUrgency;
  if (patch.scoreRisk !== undefined) row.score_risk = patch.scoreRisk;
  if (patch.scoreEffort !== undefined) row.score_effort = patch.scoreEffort;
  if (patch.stage !== undefined) row.stage = patch.stage;
  if (patch.decisionNotes !== undefined) row.decision_notes = patch.decisionNotes;
  if (patch.sizeTierId !== undefined) row.size_tier_id = patch.sizeTierId;
  if (patch.revisitDate !== undefined) row.revisit_date = patch.revisitDate;
  if (patch.convertedProjectId !== undefined) row.converted_project_id = patch.convertedProjectId;
  if (patch.pipelineProjectId !== undefined) row.pipeline_project_id = patch.pipelineProjectId;
  const { data, error } = await supabase
    .from('demands')
    .update(row)
    .eq('id', id)
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Update failed');
  return ok(fromDemandRow(data as DemandRow));
}

export async function deleteDemand(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('demands').delete().eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

interface DemandSettingsRow {
  organization_id: string;
  weight_alignment: number;
  weight_value: number;
  weight_urgency: number;
  weight_risk: number;
  weight_effort: number;
}

function fromDemandSettingsRow(r: DemandSettingsRow): DemandSettings {
  return {
    organizationId: r.organization_id,
    weightAlignment: r.weight_alignment,
    weightValue: r.weight_value,
    weightUrgency: r.weight_urgency,
    weightRisk: r.weight_risk,
    weightEffort: r.weight_effort,
  };
}

/** Returns the org's scoring weights, or null if none saved yet (use defaults). */
export async function getDemandSettings(orgId: string): Promise<ApiResult<DemandSettings | null>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('demand_settings')
    .select('*')
    .eq('organization_id', orgId)
    .maybeSingle();
  if (error) return err(error.message);
  return ok(data ? fromDemandSettingsRow(data as DemandSettingsRow) : null);
}

export async function upsertDemandSettings(
  orgId: string,
  w: Omit<DemandSettings, 'organizationId'>,
): Promise<ApiResult<DemandSettings>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('demand_settings')
    .upsert({
      organization_id: orgId,
      weight_alignment: w.weightAlignment,
      weight_value: w.weightValue,
      weight_urgency: w.weightUrgency,
      weight_risk: w.weightRisk,
      weight_effort: w.weightEffort,
    })
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Save failed');
  return ok(fromDemandSettingsRow(data as DemandSettingsRow));
}

// ---------------------------------------------------------------------------
// Lessons-Learned (paid module — Pro/Enterprise)
// ---------------------------------------------------------------------------

interface LessonRow {
  id: string;
  organization_id: string;
  project_id: string | null;
  title: string;
  category: string;
  description: string | null;
  tags: string[] | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

function fromLessonRow(r: LessonRow): Lesson {
  return {
    id: r.id,
    organizationId: r.organization_id,
    projectId: r.project_id ?? undefined,
    title: r.title,
    category: r.category as LessonCategory,
    description: r.description ?? undefined,
    tags: r.tags ?? [],
    createdBy: r.created_by ?? undefined,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export async function listLessons(orgId: string): Promise<ApiResult<Lesson[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('lessons')
    .select('*')
    .eq('organization_id', orgId)
    .order('created_at', { ascending: false });
  if (error) return err(error.message);
  return ok((data as LessonRow[]).map(fromLessonRow));
}

export async function createLesson(
  orgId: string,
  data: {
    projectId?: string | null;
    title: string;
    category: LessonCategory;
    description?: string;
    tags?: string[];
  },
): Promise<ApiResult<Lesson>> {
  if (!supabase) return err(NOT_AUTH);
  const { data: user } = await supabase.auth.getUser();
  const { data: row, error } = await supabase
    .from('lessons')
    .insert({
      organization_id: orgId,
      project_id: data.projectId ?? null,
      title: data.title,
      category: data.category,
      description: data.description ?? null,
      tags: data.tags ?? [],
      created_by: user?.user?.id ?? null,
    })
    .select()
    .single();
  if (error || !row) return err(error?.message ?? 'Create failed');
  return ok(fromLessonRow(row as LessonRow));
}

export async function updateLesson(
  id: string,
  patch: Partial<{
    projectId: string | null;
    title: string;
    category: LessonCategory;
    description: string | null;
    tags: string[];
  }>,
): Promise<ApiResult<Lesson>> {
  if (!supabase) return err(NOT_AUTH);
  const row: Record<string, unknown> = {};
  if (patch.projectId !== undefined) row.project_id = patch.projectId;
  if (patch.title !== undefined) row.title = patch.title;
  if (patch.category !== undefined) row.category = patch.category;
  if (patch.description !== undefined) row.description = patch.description;
  if (patch.tags !== undefined) row.tags = patch.tags;
  const { data, error } = await supabase
    .from('lessons')
    .update(row)
    .eq('id', id)
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Update failed');
  return ok(fromLessonRow(data as LessonRow));
}

export async function deleteLesson(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('lessons').delete().eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

/** Ask the lessons archive a question — AI retrieval over the workspace's lessons. */
export async function retrieveLessons(orgId: string, query: string): Promise<ApiResult<string>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase.functions.invoke('retrieve-lessons', {
    body: { orgId, query },
  });
  if (error) {
    let msg = 'The archive assistant is unavailable right now.';
    const ctx = (error as { context?: unknown }).context;
    if (ctx && typeof (ctx as Response).json === 'function') {
      try {
        const body = await (ctx as Response).clone().json();
        if (body?.error && typeof body.error === 'string') msg = body.error;
      } catch {
        /* keep generic */
      }
    } else if (error.message) {
      msg = error.message;
    }
    return err(msg);
  }
  if (!data || typeof data.answer !== 'string') {
    return err('The archive assistant returned an unexpected result.');
  }
  return ok(data.answer as string);
}

// Executive Briefing generation is streamed — see streamBriefing() in
// src/lib/briefing.ts (fetches the generate-briefing function and reads the
// response stream). functions.invoke() can't stream, so there's no api wrapper.

export async function listTestCases(projectId: string): Promise<ApiResult<RequirementTestCase[]>> {
  if (!supabase) return err(NOT_AUTH);
  // Fetch via requirement_id in this project
  const { data, error } = await supabase
    .from('requirement_test_cases')
    .select('*, requirements!inner(project_id)')
    .eq('requirements.project_id', projectId)
    .order('created_at', { ascending: true });
  if (error) return err(error.message);
  return ok((data as RequirementTestCaseRow[]).map(fromTestCaseRow));
}

export async function createTestCase(
  orgId: string,
  requirementId: string,
  data: {
    testCode?: string;
    testName: string;
    testSteps?: string;
    expectedResult?: string;
    testStatus?: TestCaseStatus;
  },
): Promise<ApiResult<RequirementTestCase>> {
  if (!supabase) return err(NOT_AUTH);
  const { data: row, error } = await supabase
    .from('requirement_test_cases')
    .insert({
      organization_id: orgId,
      requirement_id: requirementId,
      test_code: data.testCode ?? null,
      test_name: data.testName,
      test_steps: data.testSteps ?? null,
      expected_result: data.expectedResult ?? null,
      test_status: data.testStatus ?? 'not_tested',
    })
    .select()
    .single();
  if (error || !row) return err(error?.message ?? 'Create failed');
  return ok(fromTestCaseRow(row as RequirementTestCaseRow));
}

export async function updateTestCase(
  id: string,
  patch: Partial<{
    testCode: string | null;
    testName: string;
    testSteps: string | null;
    expectedResult: string | null;
    actualResult: string | null;
    testStatus: TestCaseStatus;
    testedBy: string | null;
    testedAt: string | null;
    notes: string | null;
  }>,
): Promise<ApiResult<RequirementTestCase>> {
  if (!supabase) return err(NOT_AUTH);
  const row: Record<string, unknown> = {};
  if (patch.testCode !== undefined) row.test_code = patch.testCode;
  if (patch.testName !== undefined) row.test_name = patch.testName;
  if (patch.testSteps !== undefined) row.test_steps = patch.testSteps;
  if (patch.expectedResult !== undefined) row.expected_result = patch.expectedResult;
  if (patch.actualResult !== undefined) row.actual_result = patch.actualResult;
  if (patch.testStatus !== undefined) row.test_status = patch.testStatus;
  if (patch.testedBy !== undefined) row.tested_by = patch.testedBy;
  if (patch.testedAt !== undefined) row.tested_at = patch.testedAt;
  if (patch.notes !== undefined) row.notes = patch.notes;
  const { data, error } = await supabase
    .from('requirement_test_cases')
    .update(row)
    .eq('id', id)
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Update failed');
  return ok(fromTestCaseRow(data as RequirementTestCaseRow));
}

export async function deleteTestCase(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('requirement_test_cases').delete().eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

// ===========================================================================
// Phase 32a: Change Request workflow + history
// ===========================================================================

interface ChangeRequestHistoryRow {
  id: string;
  change_request_id: string;
  organization_id: string;
  from_status: string | null;
  to_status: string;
  actor_id: string | null;
  actor_display: string | null;
  comment: string | null;
  created_at: string;
}

function fromCRHistoryRow(r: ChangeRequestHistoryRow): ChangeRequestHistoryEntry {
  return {
    id: r.id,
    changeRequestId: r.change_request_id,
    organizationId: r.organization_id,
    fromStatus: (r.from_status ?? undefined) as ChangeRequestStatus | undefined,
    toStatus: r.to_status as ChangeRequestStatus,
    actorId: r.actor_id ?? undefined,
    actorDisplay: r.actor_display ?? undefined,
    comment: r.comment ?? undefined,
    createdAt: r.created_at,
  };
}

export async function listChangeRequestHistory(changeRequestId: string): Promise<ApiResult<ChangeRequestHistoryEntry[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('change_request_history')
    .select('*')
    .eq('change_request_id', changeRequestId)
    .order('created_at', { ascending: true });
  if (error) return err(error.message);
  return ok((data as ChangeRequestHistoryRow[]).map(fromCRHistoryRow));
}

/**
 * Transition a change request to a new status atomically:
 *   1. Update change_requests.status + the appropriate timestamp/actor field
 *   2. Insert a change_request_history row with the transition
 * Both writes happen but are not wrapped in a single transaction — if the
 * history insert fails, the status update still went through. Acceptable
 * trade-off: state is authoritative, history is best-effort.
 */
export async function transitionChangeRequest(
  orgId: string,
  crId: string,
  fromStatus: ChangeRequestStatus,
  toStatus: ChangeRequestStatus,
  comment: string | undefined,
  actorDisplay: string | undefined,
): Promise<ApiResult<ChangeRequestHistoryEntry>> {
  if (!supabase) return err(NOT_AUTH);
  const { data: user } = await supabase.auth.getUser();
  const userId = user?.user?.id ?? null;
  const now = new Date().toISOString();

  // Update the CR row with new status + appropriate timestamp
  const crPatch: Record<string, unknown> = { status: toStatus };
  if (toStatus === 'submitted') { crPatch.submitted_by = userId; crPatch.submitted_at = now; }
  if (toStatus === 'review') { crPatch.reviewed_by = userId; crPatch.reviewed_at = now; }
  if (toStatus === 'approved') { crPatch.approved_by = userId; crPatch.approved_at = now; crPatch.decision = comment ?? ''; }
  if (toStatus === 'rejected') { crPatch.approved_by = userId; crPatch.approved_at = now; crPatch.decision = comment ?? ''; }
  if (toStatus === 'implemented') { crPatch.implemented_at = now; }
  const { error: updErr } = await supabase.from('change_requests').update(crPatch).eq('id', crId);
  if (updErr) return err(updErr.message);

  // Write history row
  const { data: histRow, error: histErr } = await supabase
    .from('change_request_history')
    .insert({
      change_request_id: crId,
      organization_id: orgId,
      from_status: fromStatus,
      to_status: toStatus,
      actor_id: userId,
      actor_display: actorDisplay ?? null,
      comment: comment ?? null,
    })
    .select()
    .single();
  if (histErr || !histRow) return err(histErr?.message ?? 'History write failed');
  return ok(fromCRHistoryRow(histRow as ChangeRequestHistoryRow));
}

interface OrgPolicyRow {
  cr_approval_roles: string[] | null;
  gate_policy: string | null;
  cr_approval_enabled: boolean | null;
  cr_approvals_required: number | null;
}

export interface OrgPolicy {
  crApprovalRoles: string[];
  gatePolicy: 'soft' | 'hard';
  /** Migration 0075: when false, CRs need no approval workflow. */
  crApprovalEnabled: boolean;
  /** Migration 0075: distinct approvers required to approve a CR (>= 1). */
  crApprovalsRequired: number;
}

const ORG_POLICY_COLS = 'cr_approval_roles, gate_policy, cr_approval_enabled, cr_approvals_required';

function toOrgPolicy(row: OrgPolicyRow): OrgPolicy {
  return {
    crApprovalRoles: row.cr_approval_roles ?? ['owner', 'admin'],
    gatePolicy: (row.gate_policy as 'soft' | 'hard') ?? 'soft',
    crApprovalEnabled: row.cr_approval_enabled ?? true,
    crApprovalsRequired: Math.max(1, row.cr_approvals_required ?? 1),
  };
}

export async function getOrgPolicy(orgId: string): Promise<ApiResult<OrgPolicy>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('organizations')
    .select(ORG_POLICY_COLS)
    .eq('id', orgId)
    .maybeSingle();
  if (error) return err(error.message);
  return ok(toOrgPolicy((data ?? {}) as OrgPolicyRow));
}

export async function updateOrgPolicy(
  orgId: string,
  patch: Partial<OrgPolicy>,
): Promise<ApiResult<OrgPolicy>> {
  if (!supabase) return err(NOT_AUTH);
  const row: Record<string, unknown> = {};
  if (patch.crApprovalRoles !== undefined) row.cr_approval_roles = patch.crApprovalRoles;
  if (patch.gatePolicy !== undefined) row.gate_policy = patch.gatePolicy;
  if (patch.crApprovalEnabled !== undefined) row.cr_approval_enabled = patch.crApprovalEnabled;
  if (patch.crApprovalsRequired !== undefined) {
    row.cr_approvals_required = Math.max(1, Math.round(patch.crApprovalsRequired));
  }
  const { data, error } = await supabase
    .from('organizations')
    .update(row)
    .eq('id', orgId)
    .select(ORG_POLICY_COLS)
    .single();
  if (error || !data) return err(error?.message ?? 'Update failed');
  return ok(toOrgPolicy(data as OrgPolicyRow));
}

/**
 * Workspace-configurable RAG health thresholds (Migration 0060). Stored as a
 * jsonb column on organizations. The read is resilient: if the column doesn't
 * exist yet (migration not applied) or any error occurs, it returns the
 * defaults so the app keeps working.
 */
export async function getOrgHealthThresholds(orgId: string): Promise<ApiResult<HealthThresholds>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('organizations')
    .select('health_thresholds')
    .eq('id', orgId)
    .maybeSingle();
  if (error) return ok({ ...DEFAULT_HEALTH_THRESHOLDS });
  const raw = ((data ?? {}) as { health_thresholds?: Partial<HealthThresholds> }).health_thresholds;
  return ok(normalizeHealthThresholds(raw ?? {}));
}

export async function updateOrgHealthThresholds(
  orgId: string,
  patch: Partial<HealthThresholds>,
): Promise<ApiResult<HealthThresholds>> {
  if (!supabase) return err(NOT_AUTH);
  // Merge onto current so a partial patch doesn't drop other keys.
  const current = await getOrgHealthThresholds(orgId);
  const base = current.data ?? DEFAULT_HEALTH_THRESHOLDS;
  const next = normalizeHealthThresholds({ ...base, ...patch });
  const { error } = await supabase
    .from('organizations')
    .update({ health_thresholds: next })
    .eq('id', orgId);
  if (error) return err(error.message);
  return ok(next);
}

/**
 * Resource management mode (Migration 0066). Stored as a text column on
 * organizations. Resilient like getOrgHealthThresholds: if the column doesn't
 * exist yet or any error occurs, returns the default ('standard') so the app
 * keeps working. See docs/resources-enterprise-model-spec.md.
 */
export async function getOrgResourceMode(orgId: string): Promise<ApiResult<ResourceMode>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('organizations')
    .select('resource_mode')
    .eq('id', orgId)
    .maybeSingle();
  if (error) return ok(DEFAULT_RESOURCE_MODE);
  const raw = ((data ?? {}) as { resource_mode?: unknown }).resource_mode;
  return ok(normalizeResourceMode(raw));
}

export async function updateOrgResourceMode(
  orgId: string,
  mode: ResourceMode,
): Promise<ApiResult<ResourceMode>> {
  if (!supabase) return err(NOT_AUTH);
  const next = normalizeResourceMode(mode);
  const { error } = await supabase
    .from('organizations')
    .update({ resource_mode: next })
    .eq('id', orgId);
  if (error) return err(error.message);
  return ok(next);
}

/**
 * Given a userId + org, return the member's role (or null if not a member).
 * Used to determine whether the current user can approve/reject a CR.
 */
export async function getMyRoleInOrg(orgId: string): Promise<ApiResult<string | null>> {
  if (!supabase) return err(NOT_AUTH);
  const { data: user } = await supabase.auth.getUser();
  const userId = user?.user?.id;
  if (!userId) return ok(null);
  const { data, error } = await supabase
    .from('organization_members')
    .select('role')
    .eq('organization_id', orgId)
    .eq('user_id', userId)
    .maybeSingle();
  if (error) return err(error.message);
  return ok(((data as { role: string } | null)?.role) ?? null);
}

// ===========================================================================
// Phase 32b: Gate templates + Project gates
// ===========================================================================

interface GateTemplateRow {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  is_archived: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

interface GateTemplateItemRow {
  id: string;
  template_id: string;
  organization_id: string;
  label: string;
  description: string | null;
  is_required: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

function fromGateTemplateRow(r: GateTemplateRow): GateTemplate {
  return {
    id: r.id,
    organizationId: r.organization_id,
    name: r.name,
    description: r.description ?? undefined,
    isArchived: r.is_archived,
    createdBy: r.created_by ?? undefined,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

function fromGateTemplateItemRow(r: GateTemplateItemRow): GateTemplateItem {
  return {
    id: r.id,
    templateId: r.template_id,
    organizationId: r.organization_id,
    label: r.label,
    description: r.description ?? undefined,
    isRequired: r.is_required,
    sortOrder: r.sort_order,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export async function listGateTemplates(orgId: string): Promise<ApiResult<GateTemplate[]>> {
  if (!supabase) return err(NOT_AUTH);
  const [tplRes, itemRes] = await Promise.all([
    supabase.from('gate_templates').select('*').eq('organization_id', orgId).order('name'),
    supabase.from('gate_template_items').select('*').eq('organization_id', orgId).order('sort_order'),
  ]);
  if (tplRes.error) return err(tplRes.error.message);
  if (itemRes.error) return err(itemRes.error.message);
  const tpls = (tplRes.data as GateTemplateRow[]).map(fromGateTemplateRow);
  const items = (itemRes.data as GateTemplateItemRow[]).map(fromGateTemplateItemRow);
  const itemsByTpl = new Map<string, GateTemplateItem[]>();
  for (const item of items) {
    if (!itemsByTpl.has(item.templateId)) itemsByTpl.set(item.templateId, []);
    itemsByTpl.get(item.templateId)!.push(item);
  }
  for (const t of tpls) t.items = itemsByTpl.get(t.id) ?? [];
  return ok(tpls);
}

export async function createGateTemplate(
  orgId: string,
  data: { name: string; description?: string },
): Promise<ApiResult<GateTemplate>> {
  if (!supabase) return err(NOT_AUTH);
  const { data: user } = await supabase.auth.getUser();
  const { data: row, error } = await supabase
    .from('gate_templates')
    .insert({
      organization_id: orgId,
      name: data.name,
      description: data.description ?? null,
      created_by: user?.user?.id ?? null,
    })
    .select()
    .single();
  if (error || !row) return err(error?.message ?? 'Create failed');
  const tpl = fromGateTemplateRow(row as GateTemplateRow);
  tpl.items = [];
  return ok(tpl);
}

export async function updateGateTemplate(
  id: string,
  patch: Partial<{ name: string; description: string | null; isArchived: boolean }>,
): Promise<ApiResult<GateTemplate>> {
  if (!supabase) return err(NOT_AUTH);
  const row: Record<string, unknown> = {};
  if (patch.name !== undefined) row.name = patch.name;
  if (patch.description !== undefined) row.description = patch.description;
  if (patch.isArchived !== undefined) row.is_archived = patch.isArchived;
  const { data, error } = await supabase
    .from('gate_templates')
    .update(row)
    .eq('id', id)
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Update failed');
  return ok(fromGateTemplateRow(data as GateTemplateRow));
}

export async function deleteGateTemplate(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('gate_templates').delete().eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

export async function createGateTemplateItem(
  orgId: string,
  templateId: string,
  data: { label: string; description?: string; isRequired?: boolean; sortOrder?: number },
): Promise<ApiResult<GateTemplateItem>> {
  if (!supabase) return err(NOT_AUTH);
  const { data: row, error } = await supabase
    .from('gate_template_items')
    .insert({
      organization_id: orgId,
      template_id: templateId,
      label: data.label,
      description: data.description ?? null,
      is_required: data.isRequired ?? true,
      sort_order: data.sortOrder ?? 0,
    })
    .select()
    .single();
  if (error || !row) return err(error?.message ?? 'Create failed');
  return ok(fromGateTemplateItemRow(row as GateTemplateItemRow));
}

export async function updateGateTemplateItem(
  id: string,
  patch: Partial<{ label: string; description: string | null; isRequired: boolean; sortOrder: number }>,
): Promise<ApiResult<GateTemplateItem>> {
  if (!supabase) return err(NOT_AUTH);
  const row: Record<string, unknown> = {};
  if (patch.label !== undefined) row.label = patch.label;
  if (patch.description !== undefined) row.description = patch.description;
  if (patch.isRequired !== undefined) row.is_required = patch.isRequired;
  if (patch.sortOrder !== undefined) row.sort_order = patch.sortOrder;
  const { data, error } = await supabase
    .from('gate_template_items')
    .update(row)
    .eq('id', id)
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Update failed');
  return ok(fromGateTemplateItemRow(data as GateTemplateItemRow));
}

export async function deleteGateTemplateItem(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('gate_template_items').delete().eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

// ---- Project gates ----

interface ProjectGateRow {
  id: string;
  project_id: string;
  organization_id: string;
  workspace_phase_id: string | null;
  template_id: string | null;
  name: string;
  requires_approval: boolean;
  status: string;
  passed_by: string | null;
  passed_at: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

interface ProjectGateItemRow {
  id: string;
  gate_id: string;
  organization_id: string;
  label: string;
  description: string | null;
  is_required: boolean;
  sort_order: number;
  is_checked: boolean;
  checked_by: string | null;
  checked_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

interface ProjectGateHistoryRow {
  id: string;
  gate_id: string;
  organization_id: string;
  from_status: string | null;
  to_status: string;
  actor_id: string | null;
  actor_display: string | null;
  comment: string | null;
  created_at: string;
}

function fromProjectGateRow(r: ProjectGateRow): ProjectGate {
  return {
    id: r.id,
    projectId: r.project_id,
    organizationId: r.organization_id,
    workspacePhaseId: r.workspace_phase_id ?? undefined,
    templateId: r.template_id ?? undefined,
    name: r.name,
    requiresApproval: r.requires_approval,
    status: r.status as ProjectGateStatus,
    passedBy: r.passed_by ?? undefined,
    passedAt: r.passed_at ?? undefined,
    notes: r.notes ?? undefined,
    createdBy: r.created_by ?? undefined,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

function fromProjectGateItemRow(r: ProjectGateItemRow): ProjectGateItem {
  return {
    id: r.id,
    gateId: r.gate_id,
    organizationId: r.organization_id,
    label: r.label,
    description: r.description ?? undefined,
    isRequired: r.is_required,
    sortOrder: r.sort_order,
    isChecked: r.is_checked,
    checkedBy: r.checked_by ?? undefined,
    checkedAt: r.checked_at ?? undefined,
    notes: r.notes ?? undefined,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

function fromProjectGateHistoryRow(r: ProjectGateHistoryRow): ProjectGateHistoryEntry {
  return {
    id: r.id,
    gateId: r.gate_id,
    organizationId: r.organization_id,
    fromStatus: (r.from_status ?? undefined) as ProjectGateStatus | undefined,
    toStatus: r.to_status as ProjectGateStatus,
    actorId: r.actor_id ?? undefined,
    actorDisplay: r.actor_display ?? undefined,
    comment: r.comment ?? undefined,
    createdAt: r.created_at,
  };
}

export async function listProjectGates(projectId: string): Promise<ApiResult<ProjectGate[]>> {
  if (!supabase) return err(NOT_AUTH);
  const gatesRes = await supabase
    .from('project_gates')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at');
  if (gatesRes.error) return err(gatesRes.error.message);
  const gates = (gatesRes.data as ProjectGateRow[]).map(fromProjectGateRow);
  if (gates.length === 0) return ok(gates);
  const gateIds = gates.map((g) => g.id);
  const itemsRes = await supabase
    .from('project_gate_items')
    .select('*')
    .in('gate_id', gateIds)
    .order('sort_order');
  if (itemsRes.error) return err(itemsRes.error.message);
  const items = (itemsRes.data as ProjectGateItemRow[]).map(fromProjectGateItemRow);
  const itemsByGate = new Map<string, ProjectGateItem[]>();
  for (const item of items) {
    if (!itemsByGate.has(item.gateId)) itemsByGate.set(item.gateId, []);
    itemsByGate.get(item.gateId)!.push(item);
  }
  for (const g of gates) g.items = itemsByGate.get(g.id) ?? [];
  return ok(gates);
}

/**
 * Create a project gate from a template — copies the template items into
 * project_gate_items in a single call. `workspacePhaseId` may be null if
 * the gate is standalone (not tied to a phase).
 */
export async function createProjectGate(
  orgId: string,
  projectId: string,
  data: {
    name: string;
    workspacePhaseId?: string;
    templateId?: string;
    requiresApproval?: boolean;
    itemsToCopy?: GateTemplateItem[];
  },
): Promise<ApiResult<ProjectGate>> {
  if (!supabase) return err(NOT_AUTH);
  const { data: user } = await supabase.auth.getUser();
  const { data: gateRow, error: gateErr } = await supabase
    .from('project_gates')
    .insert({
      organization_id: orgId,
      project_id: projectId,
      workspace_phase_id: data.workspacePhaseId ?? null,
      template_id: data.templateId ?? null,
      name: data.name,
      requires_approval: data.requiresApproval ?? false,
      created_by: user?.user?.id ?? null,
    })
    .select()
    .single();
  if (gateErr || !gateRow) return err(gateErr?.message ?? 'Create gate failed');
  const gate = fromProjectGateRow(gateRow as ProjectGateRow);

  // Copy items from template into project_gate_items
  const items = data.itemsToCopy ?? [];
  if (items.length > 0) {
    const itemRows = items.map((it) => ({
      organization_id: orgId,
      gate_id: gate.id,
      label: it.label,
      description: it.description ?? null,
      is_required: it.isRequired,
      sort_order: it.sortOrder,
    }));
    const { data: newItemsRes, error: itemsErr } = await supabase
      .from('project_gate_items')
      .insert(itemRows)
      .select();
    if (itemsErr) return err(itemsErr.message);
    gate.items = (newItemsRes as ProjectGateItemRow[]).map(fromProjectGateItemRow);
  } else {
    gate.items = [];
  }
  return ok(gate);
}

export async function updateProjectGate(
  id: string,
  patch: Partial<{ name: string; requiresApproval: boolean; notes: string | null }>,
): Promise<ApiResult<ProjectGate>> {
  if (!supabase) return err(NOT_AUTH);
  const row: Record<string, unknown> = {};
  if (patch.name !== undefined) row.name = patch.name;
  if (patch.requiresApproval !== undefined) row.requires_approval = patch.requiresApproval;
  if (patch.notes !== undefined) row.notes = patch.notes;
  const { data, error } = await supabase
    .from('project_gates')
    .update(row)
    .eq('id', id)
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Update failed');
  return ok(fromProjectGateRow(data as ProjectGateRow));
}

export async function deleteProjectGate(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('project_gates').delete().eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

export async function toggleProjectGateItem(
  itemId: string,
  isChecked: boolean,
  checkedByDisplay: string | undefined,
): Promise<ApiResult<ProjectGateItem>> {
  if (!supabase) return err(NOT_AUTH);
  const { data: user } = await supabase.auth.getUser();
  const patch: Record<string, unknown> = {
    is_checked: isChecked,
    checked_by: isChecked ? (user?.user?.id ?? null) : null,
    checked_at: isChecked ? new Date().toISOString() : null,
  };
  // Store the display name in notes if provided (audit signal)
  if (checkedByDisplay) patch.notes = isChecked ? `Checked by ${checkedByDisplay}` : null;
  const { data, error } = await supabase
    .from('project_gate_items')
    .update(patch)
    .eq('id', itemId)
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Toggle failed');
  return ok(fromProjectGateItemRow(data as ProjectGateItemRow));
}

/**
 * Transition a project gate to a new status. Writes the appropriate
 * timestamp fields on the gate row and appends a history row atomically
 * (best-effort — same pattern as CR transitions).
 */
export async function transitionProjectGate(
  orgId: string,
  gateId: string,
  fromStatus: ProjectGateStatus,
  toStatus: ProjectGateStatus,
  comment: string | undefined,
  actorDisplay: string | undefined,
): Promise<ApiResult<ProjectGateHistoryEntry>> {
  if (!supabase) return err(NOT_AUTH);
  const { data: user } = await supabase.auth.getUser();
  const userId = user?.user?.id ?? null;
  const now = new Date().toISOString();
  const gatePatch: Record<string, unknown> = { status: toStatus };
  if (toStatus === 'passed') {
    gatePatch.passed_by = userId;
    gatePatch.passed_at = now;
  }
  const { error: updErr } = await supabase.from('project_gates').update(gatePatch).eq('id', gateId);
  if (updErr) return err(updErr.message);
  const { data: histRow, error: histErr } = await supabase
    .from('project_gate_history')
    .insert({
      organization_id: orgId,
      gate_id: gateId,
      from_status: fromStatus,
      to_status: toStatus,
      actor_id: userId,
      actor_display: actorDisplay ?? null,
      comment: comment ?? null,
    })
    .select()
    .single();
  if (histErr || !histRow) return err(histErr?.message ?? 'History write failed');
  return ok(fromProjectGateHistoryRow(histRow as ProjectGateHistoryRow));
}

export async function listProjectGateHistory(gateId: string): Promise<ApiResult<ProjectGateHistoryEntry[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('project_gate_history')
    .select('*')
    .eq('gate_id', gateId)
    .order('created_at', { ascending: true });
  if (error) return err(error.message);
  return ok((data as ProjectGateHistoryRow[]).map(fromProjectGateHistoryRow));
}

// ===========================================================================
// Phase 34a: Notifications
// ===========================================================================

interface NotificationRow {
  id: string;
  organization_id: string;
  recipient_user_id: string;
  actor_user_id: string | null;
  actor_display: string | null;
  kind: string;
  entity_type: string;
  entity_id: string;
  project_id: string | null;
  title: string;
  body: string | null;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
}

function fromNotificationRow(r: NotificationRow): AppNotification {
  return {
    id: r.id,
    organizationId: r.organization_id,
    recipientUserId: r.recipient_user_id,
    actorUserId: r.actor_user_id ?? undefined,
    actorDisplay: r.actor_display ?? undefined,
    kind: r.kind as NotificationKind,
    entityType: r.entity_type as NotificationEntityType,
    entityId: r.entity_id,
    projectId: r.project_id ?? undefined,
    title: r.title,
    body: r.body ?? undefined,
    isRead: r.is_read,
    readAt: r.read_at ?? undefined,
    createdAt: r.created_at,
  };
}

/**
 * List the current user's notifications, newest first. RLS restricts to
 * the caller's own row so no explicit filter is needed.
 */
export async function listNotifications(limit = 100): Promise<ApiResult<AppNotification[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) return err(error.message);
  return ok((data as NotificationRow[]).map(fromNotificationRow));
}

/**
 * Create a notification for another workspace member. RLS enforces that the
 * caller belongs to the workspace being written to. Fails silently for
 * self-notifications (we filter out actor === recipient before calling).
 */
export async function createNotification(data: {
  organizationId: string;
  recipientUserId: string;
  actorDisplay?: string;
  kind: NotificationKind;
  entityType: NotificationEntityType;
  entityId: string;
  projectId?: string;
  title: string;
  body?: string;
}): Promise<ApiResult<AppNotification>> {
  if (!supabase) return err(NOT_AUTH);
  const { data: user } = await supabase.auth.getUser();
  const { data: row, error } = await supabase
    .from('notifications')
    .insert({
      organization_id: data.organizationId,
      recipient_user_id: data.recipientUserId,
      actor_user_id: user?.user?.id ?? null,
      actor_display: data.actorDisplay ?? null,
      kind: data.kind,
      entity_type: data.entityType,
      entity_id: data.entityId,
      project_id: data.projectId ?? null,
      title: data.title,
      body: data.body ?? null,
    })
    .select()
    .single();
  if (error || !row) return err(error?.message ?? 'Create notification failed');

  // Best-effort push to the recipient's registered devices. Fire-and-forget:
  // a push failure must never fail the in-app notification. The send-push
  // Edge Function enforces that caller and recipient share the workspace.
  void supabase.functions
    .invoke('send-push', {
      body: {
        recipient_user_id: data.recipientUserId,
        organization_id: data.organizationId,
        title: data.title,
        body: data.body ?? '',
        // Deep-link target for when the user taps the push (handled in
        // src/lib/native/push.ts -> onPushTap).
        data: data.projectId ? { path: `/projects/${data.projectId}` } : {},
      },
    })
    .catch(() => {
      /* push is non-critical */
    });

  return ok(fromNotificationRow(row as NotificationRow));
}

export async function markNotificationRead(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true, read_at: new Date().toISOString() })
    .eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

export async function markAllNotificationsRead(): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true, read_at: new Date().toISOString() })
    .eq('is_read', false);
  if (error) return err(error.message);
  return ok(null);
}

export async function deleteNotification(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('notifications').delete().eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

// ===========================================================================
// Phase 35a: Roadmap foundation (roles, assignments, pipeline projects)
// ===========================================================================

interface ResourceRoleRow {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  color: string | null;
  is_archived: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

function fromResourceRoleRow(r: ResourceRoleRow): ResourceRole {
  return {
    id: r.id,
    organizationId: r.organization_id,
    name: r.name,
    description: r.description ?? undefined,
    color: r.color ?? undefined,
    isArchived: r.is_archived,
    createdBy: r.created_by ?? undefined,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export async function listResourceRoles(orgId: string): Promise<ApiResult<ResourceRole[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('resource_roles')
    .select('*')
    .eq('organization_id', orgId)
    .order('name');
  if (error) return err(error.message);
  return ok((data as ResourceRoleRow[]).map(fromResourceRoleRow));
}

export async function createResourceRole(
  orgId: string,
  data: { name: string; description?: string; color?: string },
): Promise<ApiResult<ResourceRole>> {
  if (!supabase) return err(NOT_AUTH);
  const { data: user } = await supabase.auth.getUser();
  const { data: row, error } = await supabase
    .from('resource_roles')
    .insert({
      organization_id: orgId,
      name: data.name,
      description: data.description ?? null,
      color: data.color ?? null,
      created_by: user?.user?.id ?? null,
    })
    .select()
    .single();
  if (error || !row) return err(error?.message ?? 'Create failed');
  return ok(fromResourceRoleRow(row as ResourceRoleRow));
}

export async function updateResourceRole(
  id: string,
  patch: Partial<{ name: string; description: string | null; color: string | null; isArchived: boolean }>,
): Promise<ApiResult<ResourceRole>> {
  if (!supabase) return err(NOT_AUTH);
  const row: Record<string, unknown> = {};
  if (patch.name !== undefined) row.name = patch.name;
  if (patch.description !== undefined) row.description = patch.description;
  if (patch.color !== undefined) row.color = patch.color;
  if (patch.isArchived !== undefined) row.is_archived = patch.isArchived;
  const { data, error } = await supabase
    .from('resource_roles')
    .update(row)
    .eq('id', id)
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Update failed');
  return ok(fromResourceRoleRow(data as ResourceRoleRow));
}

export async function deleteResourceRole(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('resource_roles').delete().eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

// ===========================================================================
// Enterprise model R2: rate cards (migration 0067)
// ===========================================================================

interface RateCardRow {
  id: string;
  organization_id: string;
  role_id: string;
  grade: string | null;
  cost_rate: number | string;
  bill_rate: number | string | null;
  effective_from: string;
  effective_to: string | null;
  created_at: string;
  updated_at: string;
}

function fromRateCardRow(r: RateCardRow): RateCard {
  return {
    id: r.id,
    organizationId: r.organization_id,
    roleId: r.role_id,
    grade: r.grade ?? null,
    // numeric columns can come back as strings from PostgREST.
    costRate: typeof r.cost_rate === 'string' ? Number(r.cost_rate) : r.cost_rate,
    billRate:
      r.bill_rate == null ? null : typeof r.bill_rate === 'string' ? Number(r.bill_rate) : r.bill_rate,
    effectiveFrom: r.effective_from,
    effectiveTo: r.effective_to ?? null,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export async function listRateCards(orgId: string): Promise<ApiResult<RateCard[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('resource_rate_cards')
    .select('*')
    .eq('organization_id', orgId)
    .order('effective_from', { ascending: false });
  if (error) return err(error.message);
  return ok((data as RateCardRow[]).map(fromRateCardRow));
}

export async function createRateCard(
  orgId: string,
  d: { roleId: string; grade?: string | null; costRate: number; billRate?: number | null; effectiveFrom?: string; effectiveTo?: string | null },
): Promise<ApiResult<RateCard>> {
  if (!supabase) return err(NOT_AUTH);
  const insert: Record<string, unknown> = {
    organization_id: orgId,
    role_id: d.roleId,
    grade: d.grade ?? null,
    cost_rate: d.costRate,
    bill_rate: d.billRate ?? null,
    effective_to: d.effectiveTo ?? null,
  };
  if (d.effectiveFrom) insert.effective_from = d.effectiveFrom;
  const { data, error } = await supabase
    .from('resource_rate_cards')
    .insert(insert)
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Create failed');
  return ok(fromRateCardRow(data as RateCardRow));
}

export async function updateRateCard(
  id: string,
  patch: Partial<{ roleId: string; grade: string | null; costRate: number; billRate: number | null; effectiveFrom: string; effectiveTo: string | null }>,
): Promise<ApiResult<RateCard>> {
  if (!supabase) return err(NOT_AUTH);
  const row: Record<string, unknown> = {};
  if (patch.roleId !== undefined) row.role_id = patch.roleId;
  if (patch.grade !== undefined) row.grade = patch.grade;
  if (patch.costRate !== undefined) row.cost_rate = patch.costRate;
  if (patch.billRate !== undefined) row.bill_rate = patch.billRate;
  if (patch.effectiveFrom !== undefined) row.effective_from = patch.effectiveFrom;
  if (patch.effectiveTo !== undefined) row.effective_to = patch.effectiveTo;
  row.updated_at = new Date().toISOString();
  const { data, error } = await supabase
    .from('resource_rate_cards')
    .update(row)
    .eq('id', id)
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Update failed');
  return ok(fromRateCardRow(data as RateCardRow));
}

export async function deleteRateCard(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('resource_rate_cards').delete().eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

// ===========================================================================
// Enterprise model R3: skill proficiency levels (migration 0069)
// ===========================================================================

interface SkillLevelRow {
  id: string;
  organization_id: string;
  name_normalized: string;
  resource_id?: string | null;
  skill: string;
  level: string;
  created_at: string;
  updated_at: string;
}

function fromSkillLevelRow(r: SkillLevelRow): ResourceSkillLevel {
  return {
    id: r.id,
    organizationId: r.organization_id,
    nameNormalized: r.name_normalized,
    ...(r.resource_id ? { resourceId: r.resource_id } : {}),
    skill: r.skill,
    level: r.level as SkillLevel,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export async function listResourceSkillLevels(orgId: string): Promise<ApiResult<ResourceSkillLevel[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('resource_skill_levels')
    .select('*')
    .eq('organization_id', orgId);
  if (error) return err(error.message);
  return ok((data as SkillLevelRow[]).map(fromSkillLevelRow));
}

export async function upsertResourceSkillLevel(
  orgId: string,
  nameNormalized: string,
  skill: string,
  level: SkillLevel,
  resourceId: string | null = null,
): Promise<ApiResult<ResourceSkillLevel>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('resource_skill_levels')
    .upsert(
      { organization_id: orgId, name_normalized: nameNormalized, resource_id: resourceId, skill, level, updated_at: new Date().toISOString() },
      { onConflict: 'organization_id,name_normalized,skill' },
    )
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Save failed');
  return ok(fromSkillLevelRow(data as SkillLevelRow));
}

export async function deleteResourceSkillLevel(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('resource_skill_levels').delete().eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

/** Delete all skill-level rows for a resource name (used by rename/merge). */
export async function deleteSkillLevelsForResource(
  orgId: string,
  nameNormalized: string,
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase
    .from('resource_skill_levels')
    .delete()
    .eq('organization_id', orgId)
    .eq('name_normalized', nameNormalized);
  if (error) return err(error.message);
  return ok(null);
}

// ===========================================================================
// Enterprise model R4: departments (migration 0070)
// ===========================================================================

interface DepartmentRow {
  id: string;
  organization_id: string;
  name: string;
  cost_center: string | null;
  manager_user_id: string | null;
  parent_id: string | null;
  util_amber_pct: number | null;
  util_red_pct: number | null;
  created_at: string;
  updated_at: string;
}

function fromDepartmentRow(r: DepartmentRow): Department {
  return {
    id: r.id,
    organizationId: r.organization_id,
    name: r.name,
    costCenter: r.cost_center ?? null,
    managerUserId: r.manager_user_id ?? null,
    parentId: r.parent_id ?? null,
    utilAmberPct: r.util_amber_pct ?? null,
    utilRedPct: r.util_red_pct ?? null,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export async function listDepartments(orgId: string): Promise<ApiResult<Department[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('departments')
    .select('*')
    .eq('organization_id', orgId)
    .order('name');
  if (error) return err(error.message);
  return ok((data as DepartmentRow[]).map(fromDepartmentRow));
}

export async function createDepartment(
  orgId: string,
  d: { name: string; costCenter?: string | null; managerUserId?: string | null; parentId?: string | null; utilAmberPct?: number | null; utilRedPct?: number | null },
): Promise<ApiResult<Department>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('departments')
    .insert({
      organization_id: orgId,
      name: d.name,
      cost_center: d.costCenter ?? null,
      manager_user_id: d.managerUserId ?? null,
      parent_id: d.parentId ?? null,
      util_amber_pct: d.utilAmberPct ?? null,
      util_red_pct: d.utilRedPct ?? null,
    })
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Create failed');
  return ok(fromDepartmentRow(data as DepartmentRow));
}

export async function updateDepartment(
  id: string,
  patch: Partial<{ name: string; costCenter: string | null; managerUserId: string | null; parentId: string | null; utilAmberPct: number | null; utilRedPct: number | null }>,
): Promise<ApiResult<Department>> {
  if (!supabase) return err(NOT_AUTH);
  const row: Record<string, unknown> = {};
  if (patch.name !== undefined) row.name = patch.name;
  if (patch.costCenter !== undefined) row.cost_center = patch.costCenter;
  if (patch.managerUserId !== undefined) row.manager_user_id = patch.managerUserId;
  if (patch.parentId !== undefined) row.parent_id = patch.parentId;
  if (patch.utilAmberPct !== undefined) row.util_amber_pct = patch.utilAmberPct;
  if (patch.utilRedPct !== undefined) row.util_red_pct = patch.utilRedPct;
  row.updated_at = new Date().toISOString();
  const { data, error } = await supabase
    .from('departments')
    .update(row)
    .eq('id', id)
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Update failed');
  return ok(fromDepartmentRow(data as DepartmentRow));
}

export async function deleteDepartment(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('departments').delete().eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

// ---- Resource role assignments ----

interface ResourceRoleAssignmentRow {
  id: string;
  organization_id: string;
  resource_name: string;
  resource_id?: string | null;
  role_id: string;
  is_primary: boolean | null;
  created_at: string;
  updated_at: string;
}

function fromAssignmentRow(r: ResourceRoleAssignmentRow): ResourceRoleAssignment {
  return {
    id: r.id,
    organizationId: r.organization_id,
    resourceName: r.resource_name,
    ...(r.resource_id ? { resourceId: r.resource_id } : {}),
    roleId: r.role_id,
    isPrimary: r.is_primary ?? true,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export async function listResourceRoleAssignments(
  orgId: string,
): Promise<ApiResult<ResourceRoleAssignment[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('resource_role_assignments')
    .select('*')
    .eq('organization_id', orgId);
  if (error) return err(error.message);
  return ok((data as ResourceRoleAssignmentRow[]).map(fromAssignmentRow));
}

/**
 * Adds (or updates) a role for a named resource. Multiple roles per resource are
 * allowed (R3); `isPrimary` marks the one primary. Idempotent per (org, name,
 * role) via the unique constraint. Deleting is deleteResourceRoleAssignment.
 */
export async function upsertResourceRoleAssignment(
  orgId: string,
  resourceName: string,
  roleId: string,
  isPrimary = true,
  resourceId: string | null = null,
): Promise<ApiResult<ResourceRoleAssignment>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('resource_role_assignments')
    .upsert(
      {
        organization_id: orgId,
        resource_name: resourceName,
        resource_id: resourceId,
        role_id: roleId,
        is_primary: isPrimary,
      },
      { onConflict: 'organization_id,resource_name,role_id' },
    )
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Assign failed');
  return ok(fromAssignmentRow(data as ResourceRoleAssignmentRow));
}

/** Delete every role assignment for a resource name (used to set a sole role). */
export async function deleteAssignmentsForResource(
  orgId: string,
  resourceName: string,
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase
    .from('resource_role_assignments')
    .delete()
    .eq('organization_id', orgId)
    .eq('resource_name', resourceName);
  if (error) return err(error.message);
  return ok(null);
}

/**
 * Make `roleId` the primary role for a resource (demoting any other). Two
 * updates: clear primary on the rest, then set it on the chosen role.
 */
export async function setPrimaryResourceRole(
  orgId: string,
  resourceName: string,
  roleId: string,
): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const demote = await supabase
    .from('resource_role_assignments')
    .update({ is_primary: false })
    .eq('organization_id', orgId)
    .eq('resource_name', resourceName)
    .neq('role_id', roleId);
  if (demote.error) return err(demote.error.message);
  const promote = await supabase
    .from('resource_role_assignments')
    .update({ is_primary: true })
    .eq('organization_id', orgId)
    .eq('resource_name', resourceName)
    .eq('role_id', roleId);
  if (promote.error) return err(promote.error.message);
  return ok(null);
}

export async function deleteResourceRoleAssignment(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('resource_role_assignments').delete().eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

// ---- Pipeline projects ----

interface PipelineProjectRow {
  id: string;
  organization_id: string;
  portfolio_id: string;
  name: string;
  description: string | null;
  status: string;
  priority: string;
  business_value: number | null;
  effort_weeks: number | null;
  target_start_date: string | null;
  target_end_date: string | null;
  sort_order: number;
  source_demand_id: string | null;
  started_project_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

function fromPipelineProjectRow(r: PipelineProjectRow): PipelineProject {
  return {
    id: r.id,
    organizationId: r.organization_id,
    portfolioId: r.portfolio_id,
    name: r.name,
    description: r.description ?? undefined,
    status: r.status as PipelineProjectStatus,
    priority: r.priority as PipelineProjectPriority,
    businessValue: r.business_value ?? undefined,
    effortWeeks: r.effort_weeks ?? undefined,
    targetStartDate: r.target_start_date ?? undefined,
    targetEndDate: r.target_end_date ?? undefined,
    sortOrder: r.sort_order,
    sourceDemandId: r.source_demand_id ?? undefined,
    startedProjectId: r.started_project_id ?? undefined,
    createdBy: r.created_by ?? undefined,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export async function listPipelineProjects(
  orgId: string,
  portfolioId?: string,
): Promise<ApiResult<PipelineProject[]>> {
  if (!supabase) return err(NOT_AUTH);
  let query = supabase.from('pipeline_projects').select('*').eq('organization_id', orgId);
  if (portfolioId) query = query.eq('portfolio_id', portfolioId);
  const { data, error } = await query.order('sort_order').order('created_at');
  if (error) return err(error.message);
  return ok((data as PipelineProjectRow[]).map(fromPipelineProjectRow));
}

export async function createPipelineProject(
  orgId: string,
  data: {
    portfolioId: string;
    name: string;
    description?: string;
    status?: PipelineProjectStatus;
    priority?: PipelineProjectPriority;
    businessValue?: number;
    effortWeeks?: number;
    targetStartDate?: string;
    targetEndDate?: string;
    sortOrder?: number;
    sourceDemandId?: string;
  },
): Promise<ApiResult<PipelineProject>> {
  if (!supabase) return err(NOT_AUTH);
  const { data: user } = await supabase.auth.getUser();
  const { data: row, error } = await supabase
    .from('pipeline_projects')
    .insert({
      organization_id: orgId,
      portfolio_id: data.portfolioId,
      name: data.name,
      description: data.description ?? null,
      status: data.status ?? 'proposed',
      priority: data.priority ?? 'medium',
      business_value: data.businessValue ?? null,
      effort_weeks: data.effortWeeks ?? null,
      target_start_date: data.targetStartDate ?? null,
      target_end_date: data.targetEndDate ?? null,
      sort_order: data.sortOrder ?? 0,
      source_demand_id: data.sourceDemandId ?? null,
      created_by: user?.user?.id ?? null,
    })
    .select()
    .single();
  if (error || !row) return err(error?.message ?? 'Create failed');
  return ok(fromPipelineProjectRow(row as PipelineProjectRow));
}

export async function updatePipelineProject(
  id: string,
  patch: Partial<{
    name: string;
    description: string | null;
    status: PipelineProjectStatus;
    priority: PipelineProjectPriority;
    businessValue: number | null;
    effortWeeks: number | null;
    targetStartDate: string | null;
    targetEndDate: string | null;
    sortOrder: number;
    startedProjectId: string | null;
  }>,
): Promise<ApiResult<PipelineProject>> {
  if (!supabase) return err(NOT_AUTH);
  const row: Record<string, unknown> = {};
  if (patch.name !== undefined) row.name = patch.name;
  if (patch.description !== undefined) row.description = patch.description;
  if (patch.status !== undefined) row.status = patch.status;
  if (patch.priority !== undefined) row.priority = patch.priority;
  if (patch.businessValue !== undefined) row.business_value = patch.businessValue;
  if (patch.effortWeeks !== undefined) row.effort_weeks = patch.effortWeeks;
  if (patch.targetStartDate !== undefined) row.target_start_date = patch.targetStartDate;
  if (patch.targetEndDate !== undefined) row.target_end_date = patch.targetEndDate;
  if (patch.sortOrder !== undefined) row.sort_order = patch.sortOrder;
  if (patch.startedProjectId !== undefined) row.started_project_id = patch.startedProjectId;
  const { data, error } = await supabase
    .from('pipeline_projects')
    .update(row)
    .eq('id', id)
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Update failed');
  return ok(fromPipelineProjectRow(data as PipelineProjectRow));
}

export async function deletePipelineProject(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('pipeline_projects').delete().eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}

// ===========================================================================
// Phase 35b: Pipeline resource needs
// ===========================================================================

interface PipelineResourceNeedRow {
  id: string;
  organization_id: string;
  pipeline_project_id: string;
  role_id: string;
  count: number;
  weeks: number | null;
  start_offset_weeks: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

function fromPipelineResourceNeedRow(r: PipelineResourceNeedRow): PipelineResourceNeed {
  return {
    id: r.id,
    organizationId: r.organization_id,
    pipelineProjectId: r.pipeline_project_id,
    roleId: r.role_id,
    count: r.count,
    weeks: r.weeks ?? undefined,
    startOffsetWeeks: r.start_offset_weeks,
    notes: r.notes ?? undefined,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export async function listPipelineResourceNeeds(orgId: string): Promise<ApiResult<PipelineResourceNeed[]>> {
  if (!supabase) return err(NOT_AUTH);
  const { data, error } = await supabase
    .from('pipeline_resource_needs')
    .select('*')
    .eq('organization_id', orgId);
  if (error) return err(error.message);
  return ok((data as PipelineResourceNeedRow[]).map(fromPipelineResourceNeedRow));
}

export async function createPipelineResourceNeed(
  orgId: string,
  data: {
    pipelineProjectId: string;
    roleId: string;
    count: number;
    weeks?: number;
    startOffsetWeeks?: number;
    notes?: string;
  },
): Promise<ApiResult<PipelineResourceNeed>> {
  if (!supabase) return err(NOT_AUTH);
  const { data: row, error } = await supabase
    .from('pipeline_resource_needs')
    .insert({
      organization_id: orgId,
      pipeline_project_id: data.pipelineProjectId,
      role_id: data.roleId,
      count: data.count,
      weeks: data.weeks ?? null,
      start_offset_weeks: data.startOffsetWeeks ?? 0,
      notes: data.notes ?? null,
    })
    .select()
    .single();
  if (error || !row) return err(error?.message ?? 'Create failed');
  return ok(fromPipelineResourceNeedRow(row as PipelineResourceNeedRow));
}

export async function updatePipelineResourceNeed(
  id: string,
  patch: Partial<{ roleId: string; count: number; weeks: number | null; startOffsetWeeks: number; notes: string | null }>,
): Promise<ApiResult<PipelineResourceNeed>> {
  if (!supabase) return err(NOT_AUTH);
  const row: Record<string, unknown> = {};
  if (patch.roleId !== undefined) row.role_id = patch.roleId;
  if (patch.count !== undefined) row.count = patch.count;
  if (patch.weeks !== undefined) row.weeks = patch.weeks;
  if (patch.startOffsetWeeks !== undefined) row.start_offset_weeks = patch.startOffsetWeeks;
  if (patch.notes !== undefined) row.notes = patch.notes;
  const { data, error } = await supabase
    .from('pipeline_resource_needs')
    .update(row)
    .eq('id', id)
    .select()
    .single();
  if (error || !data) return err(error?.message ?? 'Update failed');
  return ok(fromPipelineResourceNeedRow(data as PipelineResourceNeedRow));
}

export async function deletePipelineResourceNeed(id: string): Promise<ApiResult<null>> {
  if (!supabase) return err(NOT_AUTH);
  const { error } = await supabase.from('pipeline_resource_needs').delete().eq('id', id);
  if (error) return err(error.message);
  return ok(null);
}
